import { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import api from '../services/api'
import SortableTh, { sortRows } from '../components/SortableTh'
import Paginacao, { usePaginacao } from '../components/Paginacao'
import { formatMoney } from './Apolices'
import { useNavegacaoLista } from '../hooks/useNavegacaoLista'
import SenhaMasterModal from '../components/SenhaMasterModal'
import IconExcluir from '../components/icons/IconExcluir'
import IconVisualizar from '../components/icons/IconVisualizar'
import IconEditar from '../components/icons/IconEditar'
import { avisar, confirmar } from '../components/Avisos'

const statusOptions = [
  { value: 'aberto',                    label: 'Aberto / Notificado',              cls: 'badge-blue' },
  { value: 'aguardando_vistoria',       label: 'Aguardando Vistoria',              cls: 'badge-amber' },
  { value: 'em_analise',                label: 'Em Análise / Sindicância',         cls: 'badge-amber' },
  { value: 'aprovado',                  label: 'Aprovado',                         cls: 'badge-green' },
  { value: 'liquidado',                 label: 'Liquidado / Pago',                 cls: 'badge-green' },
  { value: 'recusado',                  label: 'Recusado',                         cls: 'badge-red' },
  { value: 'encerrado_sem_indenizacao', label: 'Encerrado sem Indenização',        cls: 'badge-gray' },
  { value: 'cobertura_terceiro',        label: 'Cobertura somente para terceiro',  cls: 'badge-blue' },
]
const statusInfo = (v) => statusOptions.find(s => s.value === v) || { label: v || '—', cls: 'badge-gray' }

const tiposSinistro = ['Perda Parcial', 'Perda Total (Colisão)', 'Perda Total (Roubo)', 'Danos a Terceiros']
// No Ocorrencia do proprio Terceiro nao faz sentido classificar como "Danos a Terceiros" de novo.
const tiposSinistroTerceiro = tiposSinistro.filter(t => t !== 'Danos a Terceiros')
const tiposDano = ['Material', 'Corporal', 'Moral']
const tiposAnexoSinistro = ['Boletim de Ocorrência', 'Laudo de Vistoria', 'Fotos do Sinistro', 'Orçamento', 'Nota Fiscal', 'CNH', 'Documento do Veículo', 'Outros']

// DATE do Postgres vira JSON como "AAAA-MM-DDT00:00:00.000Z" (meia-noite UTC) — reconstruir com o
// "Z" faz o toLocaleDateString num fuso atras de UTC (Brasil) voltar 1 dia. Por isso sempre
// descarta a hora/fuso vindos do backend e ancora de novo em meia-noite LOCAL.
const fmtData = (d) => d ? new Date(d.split('T')[0] + 'T00:00:00').toLocaleDateString('pt-BR') : '—'

const maskCpf = (v) => {
  v = v.replace(/\D/g, '').slice(0, 11)
  if (v.length <= 3) return v
  if (v.length <= 6) return `${v.slice(0, 3)}.${v.slice(3)}`
  if (v.length <= 9) return `${v.slice(0, 3)}.${v.slice(3, 6)}.${v.slice(6)}`
  return `${v.slice(0, 3)}.${v.slice(3, 6)}.${v.slice(6, 9)}-${v.slice(9)}`
}
const maskPhone = v => {
  v = v.replace(/\D/g, '')
  if (v.length <= 10) { v = v.replace(/(\d{2})(\d)/, '($1) $2'); v = v.replace(/(\d{4})(\d)/, '$1-$2') }
  else { v = v.replace(/(\d{2})(\d)/, '($1) $2'); v = v.replace(/(\d{5})(\d)/, '$1-$2') }
  return v.slice(0, 15)
}
const maskCEP = v => {
  v = v.replace(/\D/g, '')
  v = v.replace(/(\d{5})(\d)/, '$1-$2')
  return v.slice(0, 9)
}

const parseMoneyLocal = v => parseFloat((v || '').replace(/[R$ .]/g, '').replace(',', '.')) || 0

// O valor de "Valor da Franquia" preenchido em Itens da apolice (Apolices.jsx > ItensAba) e uma
// cobertura dinamica (Cadastro > Coberturas) — fica salvo em coberturas_dinamicas (ja formatado
// "R$ x,xx"), a coluna legada item_valor_franquia so tem valor em itens antigos/importados de PDF.
const normalizarCampoLocal = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, ' ').trim()
const franquiaDoItem = (item) => {
  const dinamicas = item?.coberturas_dinamicas
  if (dinamicas && typeof dinamicas === 'object') {
    const chave = Object.keys(dinamicas).find(k => { const n = normalizarCampoLocal(k); return n.includes('valor') && n.includes('franquia') })
    if (chave && dinamicas[chave]) return dinamicas[chave]
  }
  if (item?.item_valor_franquia) return formatMoney(item.item_valor_franquia)
  return null
}

const sec = (t) => (
  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 12px', paddingBottom: 6, borderBottom: '2px solid var(--primary-light)' }}>{t}</div>
)

const initForm = {
  numero_sinistro: '', apolice_id: '', cliente_id: '', apolice_item_id: '',
  data_aviso: '', data_ocorrencia: '', hora_ocorrencia: '', local_fato: '', descricao: '',
  condutor_nome: '', condutor_cpf: '', condutor_nascimento: '',
  condutor_cep: '', condutor_endereco: '', condutor_numero: '', condutor_complemento: '',
  condutor_bairro: '', condutor_cidade: '', condutor_estado: '',
  tipo_sinistro: '', valor_franquia: '', valor_indenizacao: '', valor_conserto: '',
  oficina_id: '',
  status: 'aberto', tem_terceiros: false, observacoes: '',
}

// ===== Busca de Oficina com autocomplete + "+" pra cadastrar rapido (Sinistro e Terceiro usam) =====
function OficinaPicker({ oficinas, oficinaId, onSelecionar, onOficinaCriada, mostrarResumo = true }) {
  const [busca, setBusca] = useState('')
  const [mostrarSugestoes, setMostrarSugestoes] = useState(false)
  const [modalNova, setModalNova] = useState(false)

  useEffect(() => {
    const o = oficinas.find(x => String(x.id) === String(oficinaId))
    setBusca(o ? o.nome : '')
  }, [oficinaId, oficinas])

  const filtradas = busca.trim()
    ? oficinas.filter(o => (o.nome || '').toLowerCase().includes(busca.trim().toLowerCase())).slice(0, 8)
    : []
  const selecionada = oficinas.find(o => String(o.id) === String(oficinaId))
  const navBusca = useNavegacaoLista(filtradas.length, (i) => {
    const o = filtradas[i]
    onSelecionar(o.id); setBusca(o.nome); setMostrarSugestoes(false)
  })

  return (
    <div>
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <input className="form-input" value={busca}
            onChange={e => { setBusca(e.target.value); setMostrarSugestoes(true); if (!e.target.value) onSelecionar(null); navBusca.reset() }}
            onFocus={() => setMostrarSugestoes(true)}
            onBlur={() => setTimeout(() => setMostrarSugestoes(false), 150)}
            onKeyDown={navBusca.onKeyDown}
            placeholder="Buscar oficina cadastrada..." autoComplete="off" />
          {mostrarSugestoes && filtradas.length > 0 && (
            <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 20, background: '#fff', border: '1px solid #D9DDE3', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto' }}>
              {filtradas.map((o, i) => (
                <button key={o.id} type="button" onMouseDown={() => { onSelecionar(o.id); setBusca(o.nome); setMostrarSugestoes(false) }}
                  onMouseEnter={() => navBusca.setIndice(i)}
                  style={{ width: '100%', textAlign: 'left', border: 'none', background: i === navBusca.indice ? 'rgba(var(--primary-rgb),0.08)' : '#fff', padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #F1F3F5' }}>
                  <div style={{ fontSize: 13, color: '#1A1A2E', fontWeight: 600 }}>{o.nome}</div>
                  <div style={{ fontSize: 11, color: '#7A7F87', marginTop: 2 }}>{o.telefone || 'Sem telefone'}{o.responsavel ? ` · ${o.responsavel}` : ''}</div>
                </button>
              ))}
            </div>
          )}
        </div>
        <button type="button" className="btn btn-secondary" onClick={() => setModalNova(true)} title="Cadastrar nova oficina" style={{ flexShrink: 0 }}>+</button>
      </div>
      {mostrarResumo && selecionada && (
        <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
          Tel: {selecionada.telefone || '—'} · Responsável: {selecionada.responsavel || '—'}
        </div>
      )}
      {modalNova && (
        <NovaOficinaModal
          nomeInicial={busca}
          onClose={() => setModalNova(false)}
          onCriada={(nova) => { onOficinaCriada(nova); onSelecionar(nova.id); setBusca(nova.nome); setModalNova(false) }}
        />
      )}
    </div>
  )
}

function NovaOficinaModal({ nomeInicial, onClose, onCriada }) {
  const [form, setForm] = useState({ nome: nomeInicial || '', telefone: '', responsavel: '', cep: '', rua: '', numero: '', complemento: '', bairro: '', cidade: '', estado: '' })
  const [salvando, setSalvando] = useState(false)
  const [buscandoCep, setBuscandoCep] = useState(false)
  const setField = (c, v) => setForm(f => ({ ...f, [c]: v }))

  const buscarCep = async (cep) => {
    const raw = cep.replace(/\D/g, '')
    if (raw.length !== 8) return
    setBuscandoCep(true)
    try {
      const res = await fetch(`https://viacep.com.br/ws/${raw}/json/`)
      const data = await res.json()
      if (!data.erro) setForm(f => ({ ...f, rua: data.logradouro, bairro: data.bairro, cidade: data.localidade, estado: data.uf }))
    } catch { /* CEP nao encontrado, deixa o usuario preencher manualmente */ }
    finally { setBuscandoCep(false) }
  }

  const salvar = async () => {
    if (!form.nome.trim()) { avisar('Informe o nome da oficina.'); return }
    setSalvando(true)
    try {
      const { data } = await api.post('/oficinas', form)
      onCriada(data)
    } catch (err) {
      avisar('Erro ao cadastrar oficina: ' + (err.response?.data?.erro || err.message))
    } finally {
      setSalvando(false)
    }
  }

  return (
    <div className="modal-overlay" style={{ zIndex: 300 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <span className="modal-title">Nova Oficina</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="form-grid" style={{ gridTemplateColumns: '2fr 1fr' }}>
          <div className="form-group">
            <label className="form-label">Nome *</label>
            <input className="form-input" value={form.nome} onChange={e => setField('nome', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Telefone</label>
            <input className="form-input" value={form.telefone} onChange={e => setField('telefone', maskPhone(e.target.value))} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Responsável</label>
          <input className="form-input" value={form.responsavel} onChange={e => setField('responsavel', e.target.value)} />
        </div>
        <div className="form-grid" style={{ gridTemplateColumns: '1fr 2fr' }}>
          <div className="form-group">
            <label className="form-label">CEP</label>
            <input className="form-input" value={form.cep} onChange={e => { const v = maskCEP(e.target.value); setField('cep', v); if (v.length === 9) buscarCep(v) }} placeholder="00000-000" maxLength={9} />
            {buscandoCep && <span style={{ fontSize: 11, color: '#888' }}>Buscando...</span>}
          </div>
          <div className="form-group">
            <label className="form-label">Rua</label>
            <input className="form-input" value={form.rua} onChange={e => setField('rua', e.target.value)} />
          </div>
        </div>
        <div className="form-grid" style={{ gridTemplateColumns: '1fr 2fr' }}>
          <div className="form-group">
            <label className="form-label">Número</label>
            <input className="form-input" value={form.numero} onChange={e => setField('numero', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Complemento</label>
            <input className="form-input" value={form.complemento} onChange={e => setField('complemento', e.target.value)} />
          </div>
        </div>
        <div className="form-grid" style={{ gridTemplateColumns: '2fr 1fr 1fr' }}>
          <div className="form-group">
            <label className="form-label">Bairro</label>
            <input className="form-input" value={form.bairro} onChange={e => setField('bairro', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Cidade</label>
            <input className="form-input" value={form.cidade} onChange={e => setField('cidade', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Estado</label>
            <input className="form-input" value={form.estado} onChange={e => setField('estado', e.target.value.toUpperCase().slice(0, 2))} maxLength={2} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
          <button type="button" className="btn btn-primary" onClick={salvar} disabled={salvando}>{salvando ? 'Salvando...' : 'Salvar Oficina'}</button>
        </div>
      </div>
    </div>
  )
}

// ===== Histórico de Atendimento (mesmo padrão do card de negociação do CRM) — cada anotação pode
// ser registrada pro Segurado ou pra um dos Terceiros já cadastrados no sinistro. =====
function RegistrarAtendimentoSinistro({ sinistroId, onRegistrado }) {
  const [texto, setTexto] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [salvo, setSalvo] = useState(false)

  const registrar = async () => {
    if (!texto.trim()) return
    setEnviando(true)
    try {
      await api.post(`/sinistros/${sinistroId}/historico`, { texto, terceiro_id: null })
      setTexto('')
      setSalvo(true)
      setTimeout(() => setSalvo(false), 2500)
      onRegistrado?.()
    } catch (err) {
      avisar('Erro ao salvar: ' + (err.response?.data?.erro || err.message))
    } finally {
      setEnviando(false)
    }
  }

  return (
    <div className="form-group">
      <label className="form-label">Registrar atendimento</label>
      <textarea
        className="form-input" rows={5} style={{ resize: 'vertical' }}
        placeholder="Contato com o cliente, seguradora, vistoriador..."
        value={texto} onChange={e => setTexto(e.target.value)}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, marginTop: 6 }}>
        <button type="button" className="btn btn-primary btn-sm" onClick={registrar} disabled={enviando || !texto.trim()}>
          {enviando ? 'Registrando...' : 'Registrar'}
        </button>
        {salvo && <span style={{ fontSize: 12, color: '#3B6D11' }}>✓ Atendimento registrado</span>}
      </div>
    </div>
  )
}

function HistoricoSinistro({ sinistroId }) {
  const [itens, setItens] = useState([])
  const [carregando, setCarregando] = useState(true)

  // So o historico do proprio segurado — o de cada terceiro agora tem tela e historico proprios
  // (ver HistoricoTerceiro/SinistroTerceiroPage), sem mais precisar escolher "Para quem" aqui.
  const carregar = () => {
    setCarregando(true)
    api.get(`/sinistros/${sinistroId}/historico`)
      .then(r => setItens((Array.isArray(r.data) ? r.data : []).filter(h => !h.terceiro_id)))
      .catch(() => setItens([]))
      .finally(() => setCarregando(false))
  }
  useEffect(() => { carregar() }, [sinistroId])

  const remover = async (id) => {
    if (!(await confirmar('Remover esta anotação?'))) return
    await api.delete(`/sinistros/historico/${id}`)
    carregar()
  }

  const fmtDataHora = (iso) => new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
  })

  return (
    <div>
      <RegistrarAtendimentoSinistro sinistroId={sinistroId} onRegistrado={carregar} />
      {carregando ? (
        <div style={{ textAlign: 'center', color: '#aaa', padding: 20, fontSize: 13 }}>Carregando...</div>
      ) : itens.length === 0 ? (
        <div style={{ padding: 20, textAlign: 'center', color: '#aaa', background: '#f8f9fa', borderRadius: 8, fontSize: 13 }}>
          Nenhum atendimento registrado ainda.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxHeight: 320, overflowY: 'auto', paddingRight: 2 }}>
          {itens.map(h => (
            <div key={h.id} style={{ position: 'relative', background: '#f8f9fa', borderRadius: 8, borderLeft: '3px solid var(--primary)', padding: '10px 28px 10px 14px' }}>
              <button type="button" onClick={() => remover(h.id)} title="Remover" style={{
                position: 'absolute', top: 8, right: 8,
                background: 'none', border: 'none', color: '#bbb', cursor: 'pointer', fontSize: 14, lineHeight: 1
              }}>×</button>
              <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 3px' }}>
                {fmtDataHora(h.created_at)}
              </p>
              <p style={{ fontSize: 13, fontWeight: 700, color: '#2F2F2E', margin: '0 0 2px', whiteSpace: 'pre-wrap' }}>{h.texto}</p>
              <p style={{ fontSize: 11, color: '#888', margin: 0 }}>{h.usuario_nome || ''}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ===== Anexos (mesmo padrão de apolice_anexos) — reaproveitado pro sinistro do segurado
// (basePath='/sinistros') e pro anexo do Terceiro (basePath='/sinistros/terceiros') =====
function AnexosSinistro({ id, basePath = '/sinistros' }) {
  const [anexos, setAnexos] = useState([])
  const [novoAnexo, setNovoAnexo] = useState(null)
  const [enviando, setEnviando] = useState(false)

  const carregar = () => {
    api.get(`${basePath}/${id}/anexos`)
      .then(r => setAnexos(Array.isArray(r.data) ? r.data : []))
      .catch(() => setAnexos([]))
  }
  useEffect(() => { carregar() }, [id, basePath])

  const selecionarArquivo = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 5 * 1024 * 1024) { avisar('Arquivo muito grande. Máximo 5MB.'); return }
    setNovoAnexo(n => ({ ...n, arquivo: file }))
  }

  const confirmarAnexo = async () => {
    if (!novoAnexo?.arquivo) { avisar('Selecione um arquivo.'); return }
    if (!novoAnexo?.tipo) { avisar('Selecione o tipo do documento.'); return }
    setEnviando(true)
    try {
      const dados = await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = ev => resolve(ev.target.result)
        reader.onerror = reject
        reader.readAsDataURL(novoAnexo.arquivo)
      })
      await api.post(`${basePath}/${id}/anexos`, {
        tipo: novoAnexo.tipo, nome: novoAnexo.arquivo.name, tamanho: novoAnexo.arquivo.size, dados
      })
      carregar()
      setNovoAnexo(null)
    } catch (err) {
      avisar('Erro ao enviar anexo: ' + (err.response?.data?.erro || err.message))
    } finally {
      setEnviando(false)
    }
  }

  const remover = async (anexoId) => {
    if (!(await confirmar('Remover este anexo?'))) return
    await api.delete(`${basePath}/anexos/${anexoId}`)
    carregar()
  }

  const visualizar = async (a) => {
    try {
      const { data } = await api.get(`${basePath}/anexos/${a.id}/dados`)
      const parts = data.dados.split(',')
      const mimeType = parts[0].split(':')[1].split(';')[0]
      const byteStr = atob(parts[1])
      const ab = new ArrayBuffer(byteStr.length)
      const ia = new Uint8Array(ab)
      for (let x = 0; x < byteStr.length; x++) ia[x] = byteStr.charCodeAt(x)
      const blob = new Blob([ab], { type: mimeType })
      window.open(URL.createObjectURL(blob), '_blank')
    } catch (err) {
      avisar('Erro ao abrir anexo: ' + (err.response?.data?.erro || err.message))
    }
  }

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }
  const getIcon = (nome) => {
    const ext = (nome || '').split('.').pop().toLowerCase()
    if (ext === 'pdf') return '📕'
    if (['jpg', 'jpeg', 'png'].includes(ext)) return '🖼️'
    return '📄'
  }

  return (
    <>
      {anexos.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 16 }}>
          <tbody>
            {anexos.map(a => (
              <tr key={a.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '8px 10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 11, background: 'var(--primary-light)', color: 'var(--primary)', padding: '3px 8px', borderRadius: 6, fontWeight: 500, flexShrink: 0 }}>{a.tipo}</span>
                    <span style={{ fontSize: 18 }}>{getIcon(a.nome)}</span>
                    <button type="button" onClick={() => visualizar(a)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 12, color: 'var(--primary)', textDecoration: 'underline', fontFamily: 'inherit', wordBreak: 'break-all', textAlign: 'left' }}>{a.nome}</button>
                  </div>
                </td>
                <td style={{ padding: '8px 10px', fontSize: 12, color: '#888' }}>{formatSize(a.tamanho || 0)}</td>
                <td style={{ padding: '8px 10px' }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => visualizar(a)}>Visualizar</button>
                    <button type="button" className="btn btn-danger btn-sm" onClick={() => remover(a.id)}>Remover</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {anexos.length === 0 && !novoAnexo && (
        <div style={{ padding: 32, textAlign: 'center', color: '#aaa', background: '#f8f9fa', borderRadius: 8, marginBottom: 16 }}>
          Nenhum anexo adicionado ainda.
        </div>
      )}
      {novoAnexo && (
        <div style={{ background: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 12 }}>Novo Anexo</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 220px', gap: 12, marginBottom: 12 }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Arquivo</label>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={selecionarArquivo} style={{ fontSize: 13, padding: '6px 0', display: 'block', width: '100%' }} />
              {novoAnexo.arquivo && <span style={{ fontSize: 11, color: '#3B6D11', marginTop: 4, display: 'block' }}>✓ {novoAnexo.arquivo.name} ({formatSize(novoAnexo.arquivo.size)})</span>}
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Tipo do Documento</label>
              <select className="form-select" value={novoAnexo.tipo} onChange={e => setNovoAnexo(n => ({ ...n, tipo: e.target.value }))}>
                <option value="">Selecione...</option>
                {tiposAnexoSinistro.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn btn-primary btn-sm" onClick={confirmarAnexo} disabled={enviando}>{enviando ? 'Enviando...' : '✓ Salvar anexo'}</button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setNovoAnexo(null)}>Cancelar</button>
          </div>
        </div>
      )}
      {!novoAnexo && <button type="button" className="btn btn-secondary" onClick={() => setNovoAnexo({ arquivo: null, tipo: '' })}>+ Adicionar novo anexo</button>}
      <div style={{ marginTop: 12, fontSize: 11, color: '#aaa' }}>Formatos aceitos: PDF, JPG, PNG. Tamanho máximo: 5MB por arquivo.</div>
    </>
  )
}

const subtiposDanoMaterial = ['Veículo', 'Residência', 'Objeto']

const initTerceiro = {
  nome: '', cpf_cnpj: '', telefone: '',
  numero_sinistro: '',
  tipo_dano: '', subtipo_dano: '',
  veiculo_modelo: '', veiculo_ano_modelo: '', veiculo_placa: '', veiculo_chassi: '',
  endereco_cep: '', endereco_rua: '', endereco_numero: '', endereco_complemento: '', endereco_bairro: '', endereco_cidade: '', endereco_estado: '',
  objeto_especificacao: '', dano_especificacao: '',
  data_aviso: '', data_ocorrencia: '', hora_ocorrencia: '', tipo_sinistro: '', local_fato: '', descricao: '',
  condutor_nome: '', condutor_cpf: '', condutor_nascimento: '',
  condutor_cep: '', condutor_endereco: '', condutor_numero: '', condutor_complemento: '', condutor_bairro: '', condutor_cidade: '', condutor_estado: '',
  valor_indenizacao: '', oficina_id: '', status: 'aberto', observacoes: '',
}

const subLabel = (t) => (
  <div style={{ fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '16px 0 8px' }}>{t}</div>
)
// Mesmo estilo do subLabel, so que sublinhado — usado no Resumo do Sinistro pra destacar melhor
// cada subtitulo em meio aos campos (ficavam meio misturados so em cinza).
const subLabelSublinhado = (t) => (
  <div style={{ fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '16px 0 8px', textDecoration: 'underline', textUnderlineOffset: 3 }}>{t}</div>
)

// ===== Terceiros (RCF) — só existem vinculados a um sinistro do segurado já salvo. Formulario
// espelha o do sinistro do segurado (Dados/Ocorrencia/Condutor/Valores/Oficina/Anexos), com secoes
// que aparecem ou somem conforme o tipo/subtipo de dano escolhido. =====
function TerceirosSinistro({ sinistroId, temTerceiros, oficinas, onOficinaCriada, onVer }) {
  const [terceiros, setTerceiros] = useState([])
  const [novo, setNovo] = useState(null)
  const [salvando, setSalvando] = useState(false)

  const carregar = () => {
    api.get(`/sinistros/${sinistroId}/terceiros`)
      .then(r => setTerceiros(Array.isArray(r.data) ? r.data : []))
      .catch(() => setTerceiros([]))
  }
  useEffect(() => { carregar() }, [sinistroId])

  const setC = (campo, valor) => setNovo(n => ({ ...n, [campo]: valor }))

  const buscarCepEndereco = async (cep) => {
    const raw = cep.replace(/\D/g, '')
    if (raw.length !== 8) return
    try {
      const res = await fetch(`https://viacep.com.br/ws/${raw}/json/`)
      const data = await res.json()
      if (!data.erro) setNovo(n => ({ ...n, endereco_rua: data.logradouro, endereco_bairro: data.bairro, endereco_cidade: data.localidade, endereco_estado: data.uf }))
    } catch { /* CEP nao encontrado */ }
  }
  const buscarCepCondutorTerceiro = async (cep) => {
    const raw = cep.replace(/\D/g, '')
    if (raw.length !== 8) return
    try {
      const res = await fetch(`https://viacep.com.br/ws/${raw}/json/`)
      const data = await res.json()
      if (!data.erro) setNovo(n => ({ ...n, condutor_endereco: data.logradouro, condutor_bairro: data.bairro, condutor_cidade: data.localidade, condutor_estado: data.uf }))
    } catch { /* CEP nao encontrado */ }
  }

  const salvar = async () => {
    if (!novo.nome?.trim()) { avisar('Informe o nome do terceiro.'); return }
    // Editar um terceiro ja existente fecha a janela ao salvar, voltando so pro resumo; um
    // terceiro novo continua aberto (permite anexar arquivos logo em seguida).
    const eraEdicao = !!novo.id
    setSalvando(true)
    try {
      if (novo.id) await api.put(`/sinistros/terceiros/${novo.id}`, novo)
      else {
        const { data } = await api.post(`/sinistros/${sinistroId}/terceiros`, novo)
        setNovo(n => ({ ...n, id: data.id }))
      }
      carregar()
      if (eraEdicao) setNovo(null)
    } catch (err) {
      avisar('Erro ao salvar terceiro: ' + (err.response?.data?.erro || err.message))
    } finally {
      setSalvando(false)
    }
  }

  if (!temTerceiros) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: '#aaa', background: '#f8f9fa', borderRadius: 8, fontSize: 13 }}>
        Marque "Houve terceiros envolvidos?" na aba Dados para poder registrar terceiros (RCF).
      </div>
    )
  }

  const isVeiculo = novo?.tipo_dano === 'Material' && novo?.subtipo_dano === 'Veículo'
  const isResidencia = novo?.tipo_dano === 'Material' && novo?.subtipo_dano === 'Residência'
  const isObjeto = novo?.tipo_dano === 'Material' && novo?.subtipo_dano === 'Objeto'
  const isCorporalOuMoral = novo?.tipo_dano === 'Corporal' || novo?.tipo_dano === 'Moral'

  return (
    <div>
      {terceiros.length === 0 && !novo && (
        <div style={{ padding: 24, textAlign: 'center', color: '#aaa', background: '#f8f9fa', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>
          Nenhum terceiro registrado ainda.
        </div>
      )}
      {terceiros.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
          {terceiros.map(t => {
            const st = statusInfo(t.status)
            const isVeic = t.tipo_dano === 'Material' && t.subtipo_dano === 'Veículo'
            const especificacao = isVeic
              ? [t.veiculo_modelo, t.veiculo_placa ? `Placa ${t.veiculo_placa}` : '', t.veiculo_chassi ? `Chassi ${t.veiculo_chassi}` : ''].filter(Boolean).join(' · ')
              : t.subtipo_dano === 'Residência'
                ? [t.endereco_rua, t.endereco_numero, t.endereco_cidade].filter(Boolean).join(', ')
                : (t.objeto_especificacao || t.dano_especificacao || '')
            return (
              <div key={t.id} style={{ background: '#F8F9FA', borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>{t.nome} {t.tipo_dano && <span style={{ fontWeight: 400, color: '#888' }}>· Dano {t.tipo_dano}{t.subtipo_dano ? ` (${t.subtipo_dano})` : ''}</span>}</div>
                    {t.numero_sinistro && <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>Nº Sinistro: {t.numero_sinistro}</div>}
                    <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                      {t.cpf_cnpj || '—'} {t.telefone ? `· ${t.telefone}` : ''}
                    </div>
                    {especificacao && <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>{especificacao}</div>}
                    <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                      {t.valor_indenizacao ? `Conserto/Indenização: ${formatMoney(t.valor_indenizacao)}` : ''}
                    </div>
                    {t.oficina_nome && (
                      <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                        Oficina: {t.oficina_nome}{t.oficina_telefone ? ` · ${t.oficina_telefone}` : ''}{t.oficina_responsavel ? ` · Resp.: ${t.oficina_responsavel}` : ''}
                      </div>
                    )}
                  </div>
                  <span className={`badge ${st.cls}`} style={{ flexShrink: 0 }}>{st.label}</span>
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  <button type="button" className="btn btn-secondary btn-sm btn-icon" title="Ver" style={{ color: 'var(--primary)' }} onClick={() => onVer(t)}><IconVisualizar size={14} /></button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {novo ? (
        <div style={{ background: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: 10, padding: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 4 }}>{novo.id ? 'Editar Terceiro' : 'Novo Terceiro'}</div>

          {subLabel('Dados')}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Nome *</label>
              <input className="form-input" value={novo.nome} onChange={e => setC('nome', e.target.value)} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">CPF</label>
              <input className="form-input" value={novo.cpf_cnpj} onChange={e => setC('cpf_cnpj', maskCpf(e.target.value))} placeholder="000.000.000-00" maxLength={14} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Telefone</label>
              <input className="form-input" value={novo.telefone} onChange={e => setC('telefone', maskPhone(e.target.value))} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: novo.tipo_dano === 'Material' ? '1fr 1fr 1fr' : '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Nº do Sinistro</label>
              <input className="form-input" value={novo.numero_sinistro} onChange={e => setC('numero_sinistro', e.target.value)} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Tipo de Dano</label>
              <select className="form-select" value={novo.tipo_dano} onChange={e => setC('tipo_dano', e.target.value)}>
                <option value="">Selecione...</option>
                {tiposDano.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            {novo.tipo_dano === 'Material' && (
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Material — qual?</label>
                <select className="form-select" value={novo.subtipo_dano} onChange={e => setC('subtipo_dano', e.target.value)}>
                  <option value="">Selecione...</option>
                  {subtiposDanoMaterial.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
            )}
          </div>

          {isVeiculo && (
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Modelo do Veículo</label>
                <input className="form-input" value={novo.veiculo_modelo} onChange={e => setC('veiculo_modelo', e.target.value)} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Ano/Modelo</label>
                <input className="form-input" value={novo.veiculo_ano_modelo} onChange={e => setC('veiculo_ano_modelo', e.target.value)} maxLength={4} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Placa</label>
                <input className="form-input" value={novo.veiculo_placa} onChange={e => setC('veiculo_placa', e.target.value)} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Chassi</label>
                <input className="form-input" value={novo.veiculo_chassi} onChange={e => setC('veiculo_chassi', e.target.value)} />
              </div>
            </div>
          )}

          {isResidencia && (<>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">CEP</label>
                <input className="form-input" value={novo.endereco_cep} onChange={e => { const v = maskCEP(e.target.value); setC('endereco_cep', v); if (v.length === 9) buscarCepEndereco(v) }} placeholder="00000-000" maxLength={9} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Rua</label>
                <input className="form-input" value={novo.endereco_rua} onChange={e => setC('endereco_rua', e.target.value)} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Número</label>
                <input className="form-input" value={novo.endereco_numero} onChange={e => setC('endereco_numero', e.target.value)} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Complemento</label>
                <input className="form-input" value={novo.endereco_complemento} onChange={e => setC('endereco_complemento', e.target.value)} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Bairro</label>
                <input className="form-input" value={novo.endereco_bairro} onChange={e => setC('endereco_bairro', e.target.value)} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Cidade</label>
                <input className="form-input" value={novo.endereco_cidade} onChange={e => setC('endereco_cidade', e.target.value)} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Estado</label>
                <input className="form-input" value={novo.endereco_estado} onChange={e => setC('endereco_estado', e.target.value.toUpperCase().slice(0, 2))} maxLength={2} />
              </div>
            </div>
          </>)}

          {isObjeto && (
            <div className="form-group">
              <label className="form-label">Especifique o objeto</label>
              <input className="form-input" value={novo.objeto_especificacao} onChange={e => setC('objeto_especificacao', e.target.value)} />
            </div>
          )}

          {isCorporalOuMoral && (
            <div className="form-group">
              <label className="form-label">Especifique o dano</label>
              <input className="form-input" value={novo.dano_especificacao} onChange={e => setC('dano_especificacao', e.target.value)} />
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Status</label>
              <select className="form-select" value={novo.status} onChange={e => setC('status', e.target.value)}>
                {statusOptions.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Observações</label>
            <textarea className="form-input" rows={2} style={{ resize: 'vertical' }} value={novo.observacoes} onChange={e => setC('observacoes', e.target.value)} />
          </div>

          {novo.tipo_dano && (<>
            {subLabel('Ocorrência')}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Data do Aviso</label>
                <input className="form-input" type="date" value={novo.data_aviso} onChange={e => setC('data_aviso', e.target.value)} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Data da Ocorrência</label>
                <input className="form-input" type="date" value={novo.data_ocorrencia} onChange={e => setC('data_ocorrencia', e.target.value)} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Hora da Ocorrência</label>
                <input className="form-input" type="time" value={novo.hora_ocorrencia} onChange={e => setC('hora_ocorrencia', e.target.value)} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Tipo de Sinistro</label>
                <select className="form-select" value={novo.tipo_sinistro} onChange={e => setC('tipo_sinistro', e.target.value)}>
                  <option value="">Selecione...</option>
                  {tiposSinistroTerceiro.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
            </div>
            {isVeiculo && (
              <div className="form-group">
                <label className="form-label">Local do Fato</label>
                <input className="form-input" value={novo.local_fato} onChange={e => setC('local_fato', e.target.value)} />
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Descrição do Ocorrido</label>
              <textarea className="form-input" rows={2} style={{ resize: 'vertical' }} value={novo.descricao} onChange={e => setC('descricao', e.target.value)} />
            </div>
          </>)}

          {isVeiculo && (<>
            {subLabel('Condutor no momento do sinistro')}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Nome</label>
                <input className="form-input" value={novo.condutor_nome} onChange={e => setC('condutor_nome', e.target.value)} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">CPF</label>
                <input className="form-input" value={novo.condutor_cpf} onChange={e => setC('condutor_cpf', maskCpf(e.target.value))} placeholder="000.000.000-00" maxLength={14} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Nascimento</label>
                <input className="form-input" type="date" value={novo.condutor_nascimento} onChange={e => setC('condutor_nascimento', e.target.value)} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">CEP</label>
                <input className="form-input" value={novo.condutor_cep} onChange={e => { const v = maskCEP(e.target.value); setC('condutor_cep', v); if (v.length === 9) buscarCepCondutorTerceiro(v) }} placeholder="00000-000" maxLength={9} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Rua</label>
                <input className="form-input" value={novo.condutor_endereco} onChange={e => setC('condutor_endereco', e.target.value)} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Número</label>
                <input className="form-input" value={novo.condutor_numero} onChange={e => setC('condutor_numero', e.target.value)} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Complemento</label>
                <input className="form-input" value={novo.condutor_complemento} onChange={e => setC('condutor_complemento', e.target.value)} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Bairro</label>
                <input className="form-input" value={novo.condutor_bairro} onChange={e => setC('condutor_bairro', e.target.value)} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Cidade</label>
                <input className="form-input" value={novo.condutor_cidade} onChange={e => setC('condutor_cidade', e.target.value)} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Estado</label>
                <input className="form-input" value={novo.condutor_estado} onChange={e => setC('condutor_estado', e.target.value.toUpperCase().slice(0, 2))} maxLength={2} />
              </div>
            </div>
          </>)}

          {subLabel('Valores')}
          <div className="form-group">
            <label className="form-label">Valor do Conserto/Indenização</label>
            <input className="form-input" value={novo.valor_indenizacao}
              onChange={e => setC('valor_indenizacao', e.target.value)}
              onBlur={e => setC('valor_indenizacao', e.target.value.trim() === '' ? '' : formatMoney(parseMoneyLocal(e.target.value)))}
              placeholder="R$ 0,00" style={{ maxWidth: 260 }} />
          </div>

          {isVeiculo && (<>
            {subLabel('Oficina')}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Nome</label>
                <OficinaPicker oficinas={oficinas} oficinaId={novo.oficina_id} onSelecionar={id => setC('oficina_id', id)} onOficinaCriada={onOficinaCriada} mostrarResumo={false} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Telefone</label>
                <input className="form-input" value={oficinas.find(o => String(o.id) === String(novo.oficina_id))?.telefone || ''} disabled placeholder="—" style={{ background: '#f0f0f0', color: '#666' }} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Responsável</label>
                <input className="form-input" value={oficinas.find(o => String(o.id) === String(novo.oficina_id))?.responsavel || ''} disabled placeholder="—" style={{ background: '#f0f0f0', color: '#666' }} />
              </div>
            </div>
          </>)}

          {subLabel('Anexos')}
          {novo.id
            ? <AnexosSinistro id={novo.id} basePath="/sinistros/terceiros" />
            : <div style={{ padding: 14, textAlign: 'center', color: '#aaa', background: '#fff', borderRadius: 8, fontSize: 12 }}>Salve o terceiro primeiro.</div>}

          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button type="button" className="btn btn-primary btn-sm" onClick={salvar} disabled={salvando}>{salvando ? 'Salvando...' : '✓ Salvar terceiro'}</button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setNovo(null); carregar() }}>Fechar</button>
          </div>
        </div>
      ) : (
        <button type="button" className="btn btn-secondary" onClick={() => setNovo(initTerceiro)}>+ Adicionar Terceiro</button>
      )}
    </div>
  )
}

// ===== Histórico de Atendimento do Terceiro — mesmo padrao de HistoricoSinistro, mas so mostra
// (e so registra) anotacoes desse terceiro especifico, sem o seletor "Para o Segurado/Terceiro". =====
function HistoricoTerceiro({ sinistroId, terceiroId }) {
  const [itens, setItens] = useState([])
  const [carregando, setCarregando] = useState(true)
  const [texto, setTexto] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [salvo, setSalvo] = useState(false)

  const carregar = () => {
    setCarregando(true)
    api.get(`/sinistros/${sinistroId}/historico`)
      .then(r => setItens((Array.isArray(r.data) ? r.data : []).filter(h => String(h.terceiro_id) === String(terceiroId))))
      .catch(() => setItens([]))
      .finally(() => setCarregando(false))
  }
  useEffect(() => { carregar() }, [sinistroId, terceiroId])

  const registrar = async () => {
    if (!texto.trim()) return
    setEnviando(true)
    try {
      await api.post(`/sinistros/${sinistroId}/historico`, { texto, terceiro_id: terceiroId })
      setTexto('')
      setSalvo(true)
      setTimeout(() => setSalvo(false), 2500)
      carregar()
    } catch (err) {
      avisar('Erro ao salvar: ' + (err.response?.data?.erro || err.message))
    } finally {
      setEnviando(false)
    }
  }

  const remover = async (id) => {
    if (!(await confirmar('Remover esta anotação?'))) return
    await api.delete(`/sinistros/historico/${id}`)
    carregar()
  }

  const fmtDataHora = (iso) => new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
  })

  return (
    <div>
      <div className="form-group">
        <label className="form-label">Registrar atendimento</label>
        <textarea
          className="form-input" rows={5} style={{ resize: 'vertical' }}
          placeholder="Contato com o terceiro, seguradora, vistoriador..."
          value={texto} onChange={e => setTexto(e.target.value)}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, marginTop: 6 }}>
          <button type="button" className="btn btn-primary btn-sm" onClick={registrar} disabled={enviando || !texto.trim()}>
            {enviando ? 'Registrando...' : 'Registrar'}
          </button>
          {salvo && <span style={{ fontSize: 12, color: '#3B6D11' }}>✓ Atendimento registrado</span>}
        </div>
      </div>
      {carregando ? (
        <div style={{ textAlign: 'center', color: '#aaa', padding: 20, fontSize: 13 }}>Carregando...</div>
      ) : itens.length === 0 ? (
        <div style={{ padding: 20, textAlign: 'center', color: '#aaa', background: '#f8f9fa', borderRadius: 8, fontSize: 13 }}>
          Nenhum atendimento registrado ainda.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxHeight: 320, overflowY: 'auto', paddingRight: 2 }}>
          {itens.map(h => (
            <div key={h.id} style={{ position: 'relative', background: '#f8f9fa', borderRadius: 8, borderLeft: '3px solid #F5A623', padding: '10px 28px 10px 14px' }}>
              <button type="button" onClick={() => remover(h.id)} title="Remover" style={{
                position: 'absolute', top: 8, right: 8,
                background: 'none', border: 'none', color: '#bbb', cursor: 'pointer', fontSize: 14, lineHeight: 1
              }}>×</button>
              <p style={{ fontSize: 10, fontWeight: 700, color: '#F5A623', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 3px' }}>
                {fmtDataHora(h.created_at)}
              </p>
              <p style={{ fontSize: 13, fontWeight: 700, color: '#2F2F2E', margin: '0 0 2px', whiteSpace: 'pre-wrap' }}>{h.texto}</p>
              <p style={{ fontSize: 11, color: '#888', margin: 0 }}>{h.usuario_nome || ''}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ===== Tela propria do Sinistro do Terceiro — mesmo padrao visual da tela do sinistro do segurado
// (SinistroModal), mas com os dados de sinistro_terceiros. Ligada ao sinistro do segurado por
// sinistro_id (nunca existe sozinha), com um link de volta pra abrir o sinistro do segurado. =====
function SinistroTerceiroModal({ terceiroId, onClose, onVerSegurado }) {
  const [t, setT] = useState(null)
  const [carregando, setCarregando] = useState(true)
  const [modoEdicao, setModoEdicao] = useState(false)
  const [novo, setNovo] = useState(null)
  const [oficinas, setOficinas] = useState([])
  const [salvando, setSalvando] = useState(false)

  useEffect(() => {
    api.get('/oficinas').then(r => setOficinas(Array.isArray(r.data) ? r.data : [])).catch(() => setOficinas([]))
  }, [])
  const onOficinaCriada = (nova) => setOficinas(prev => [...prev, nova])

  const carregar = () => {
    setCarregando(true)
    api.get(`/sinistros/terceiros/${terceiroId}`)
      .then(r => setT(r.data))
      .catch(() => setT(null))
      .finally(() => setCarregando(false))
  }
  useEffect(() => { carregar() }, [terceiroId])

  const abrirEdicao = () => {
    setNovo({
      ...initTerceiro,
      ...t,
      data_aviso: t.data_aviso?.split('T')[0] || '',
      data_ocorrencia: t.data_ocorrencia?.split('T')[0] || '',
      condutor_nascimento: t.condutor_nascimento?.split('T')[0] || '',
      valor_indenizacao: t.valor_indenizacao != null ? formatMoney(t.valor_indenizacao) : '',
    })
    setModoEdicao(true)
  }
  const setC = (campo, valor) => setNovo(n => ({ ...n, [campo]: valor }))

  const buscarCepEndereco = async (cep) => {
    const raw = cep.replace(/\D/g, '')
    if (raw.length !== 8) return
    try {
      const res = await fetch(`https://viacep.com.br/ws/${raw}/json/`)
      const data = await res.json()
      if (!data.erro) setNovo(n => ({ ...n, endereco_rua: data.logradouro, endereco_bairro: data.bairro, endereco_cidade: data.localidade, endereco_estado: data.uf }))
    } catch { /* CEP nao encontrado */ }
  }
  const buscarCepCondutorTerceiro = async (cep) => {
    const raw = cep.replace(/\D/g, '')
    if (raw.length !== 8) return
    try {
      const res = await fetch(`https://viacep.com.br/ws/${raw}/json/`)
      const data = await res.json()
      if (!data.erro) setNovo(n => ({ ...n, condutor_endereco: data.logradouro, condutor_bairro: data.bairro, condutor_cidade: data.localidade, condutor_estado: data.uf }))
    } catch { /* CEP nao encontrado */ }
  }

  const salvar = async () => {
    if (!novo.nome?.trim()) { avisar('Informe o nome do terceiro.'); return }
    setSalvando(true)
    try {
      await api.put(`/sinistros/terceiros/${terceiroId}`, novo)
      carregar()
      setModoEdicao(false)
    } catch (err) {
      avisar('Erro ao salvar terceiro: ' + (err.response?.data?.erro || err.message))
    } finally {
      setSalvando(false)
    }
  }

  const cancelarEdicao = () => setModoEdicao(false)

  const excluir = async () => {
    if (!(await confirmar('Remover este terceiro? Essa ação não pode ser desfeita.'))) return
    await api.delete(`/sinistros/terceiros/${terceiroId}`)
    onClose()
  }

  if (carregando) return <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Carregando...</div>
  if (!t) return (
    <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>
      Terceiro não encontrado.
      <div style={{ marginTop: 12 }}><button type="button" className="btn btn-secondary" onClick={onClose}>‹ Voltar para Sinistros</button></div>
    </div>
  )

  const st = statusInfo(modoEdicao ? novo.status : t.status)
  const fonte = modoEdicao ? novo : t
  const isVeiculo = fonte.tipo_dano === 'Material' && fonte.subtipo_dano === 'Veículo'
  const isResidencia = fonte.tipo_dano === 'Material' && fonte.subtipo_dano === 'Residência'
  const isObjeto = fonte.tipo_dano === 'Material' && fonte.subtipo_dano === 'Objeto'
  const isCorporalOuMoral = fonte.tipo_dano === 'Corporal' || fonte.tipo_dano === 'Moral'
  const oficinaObj = oficinas.find(o => String(o.id) === String(t.oficina_id)) || null

  const segurdoLink = (
    <button type="button" onClick={() => onVerSegurado(t.sinistro_id)} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--primary)', textDecoration: 'underline', cursor: 'pointer', fontSize: 12 }}>
      {t.segurado_nome}{t.sinistro_numero_segurado ? ` — nº ${t.sinistro_numero_segurado}` : ''}
    </button>
  )

  return (
    <div style={{ padding: '24px 24px 40px' }}>
      <div style={{ marginBottom: 14 }}>
        <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', fontSize: 13, fontWeight: 600, padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
          ‹ Voltar para Sinistros
        </button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <h2 style={{ margin: '8px 0 0', fontSize: 22, fontWeight: 700 }}>Sinistro do Terceiro{t.numero_sinistro ? ` nº ${t.numero_sinistro}` : ''}</h2>
          {!modoEdicao && (
            <div style={{ display: 'flex', gap: 10, marginTop: 8, flexShrink: 0 }}>
              <button type="button" className="btn btn-danger btn-icon" title="Excluir" onClick={excluir}><IconExcluir size={16} /></button>
              <button type="button" className="btn btn-primary btn-icon" title="Editar" onClick={abrirEdicao}><IconEditar size={16} /></button>
            </div>
          )}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <span style={{ fontSize: 22, fontWeight: 700, color: 'var(--primary)', letterSpacing: '0.01em' }}>{t.nome}</span>
          <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Deriva do sinistro do segurado {segurdoLink}</div>
        </div>
        <span className={`badge ${st.cls}`}>{st.label}</span>
      </div>

      {modoEdicao && novo && (
        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 20, minWidth: 0 }}>
            <div className="card" style={{ paddingTop: 14, paddingBottom: 16 }}>
              {sec('Dados')}
              <div className="form-grid" style={{ gridTemplateColumns: '2fr 1fr 1fr' }}>
                <div className="form-group">
                  <label className="form-label">Nome *</label>
                  <input className="form-input" value={novo.nome} onChange={e => setC('nome', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">CPF</label>
                  <input className="form-input" value={novo.cpf_cnpj} onChange={e => setC('cpf_cnpj', maskCpf(e.target.value))} placeholder="000.000.000-00" maxLength={14} />
                </div>
                <div className="form-group">
                  <label className="form-label">Telefone</label>
                  <input className="form-input" value={novo.telefone} onChange={e => setC('telefone', maskPhone(e.target.value))} />
                </div>
              </div>
              <div className="form-grid" style={{ gridTemplateColumns: novo.tipo_dano === 'Material' ? '1fr 1fr 1fr' : '1fr 1fr' }}>
                <div className="form-group">
                  <label className="form-label">Nº do Sinistro</label>
                  <input className="form-input" value={novo.numero_sinistro} onChange={e => setC('numero_sinistro', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Tipo de Dano</label>
                  <select className="form-select" value={novo.tipo_dano} onChange={e => setC('tipo_dano', e.target.value)}>
                    <option value="">Selecione...</option>
                    {tiposDano.map(x => <option key={x}>{x}</option>)}
                  </select>
                </div>
                {novo.tipo_dano === 'Material' && (
                  <div className="form-group">
                    <label className="form-label">Material — qual?</label>
                    <select className="form-select" value={novo.subtipo_dano} onChange={e => setC('subtipo_dano', e.target.value)}>
                      <option value="">Selecione...</option>
                      {subtiposDanoMaterial.map(x => <option key={x}>{x}</option>)}
                    </select>
                  </div>
                )}
              </div>

              {isVeiculo && (
                <div className="form-grid" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr' }}>
                  <div className="form-group">
                    <label className="form-label">Modelo do Veículo</label>
                    <input className="form-input" value={novo.veiculo_modelo} onChange={e => setC('veiculo_modelo', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Ano/Modelo</label>
                    <input className="form-input" value={novo.veiculo_ano_modelo} onChange={e => setC('veiculo_ano_modelo', e.target.value)} maxLength={4} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Placa</label>
                    <input className="form-input" value={novo.veiculo_placa} onChange={e => setC('veiculo_placa', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Chassi</label>
                    <input className="form-input" value={novo.veiculo_chassi} onChange={e => setC('veiculo_chassi', e.target.value)} />
                  </div>
                </div>
              )}

              {isResidencia && (<>
                <div className="form-grid" style={{ gridTemplateColumns: '1fr 2fr 1fr 1fr' }}>
                  <div className="form-group">
                    <label className="form-label">CEP</label>
                    <input className="form-input" value={novo.endereco_cep} onChange={e => { const v = maskCEP(e.target.value); setC('endereco_cep', v); if (v.length === 9) buscarCepEndereco(v) }} placeholder="00000-000" maxLength={9} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Rua</label>
                    <input className="form-input" value={novo.endereco_rua} onChange={e => setC('endereco_rua', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Número</label>
                    <input className="form-input" value={novo.endereco_numero} onChange={e => setC('endereco_numero', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Complemento</label>
                    <input className="form-input" value={novo.endereco_complemento} onChange={e => setC('endereco_complemento', e.target.value)} />
                  </div>
                </div>
                <div className="form-grid" style={{ gridTemplateColumns: '2fr 1fr 1fr' }}>
                  <div className="form-group">
                    <label className="form-label">Bairro</label>
                    <input className="form-input" value={novo.endereco_bairro} onChange={e => setC('endereco_bairro', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Cidade</label>
                    <input className="form-input" value={novo.endereco_cidade} onChange={e => setC('endereco_cidade', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Estado</label>
                    <input className="form-input" value={novo.endereco_estado} onChange={e => setC('endereco_estado', e.target.value.toUpperCase().slice(0, 2))} maxLength={2} />
                  </div>
                </div>
              </>)}

              {isObjeto && (
                <div className="form-group">
                  <label className="form-label">Especifique o objeto</label>
                  <input className="form-input" value={novo.objeto_especificacao} onChange={e => setC('objeto_especificacao', e.target.value)} />
                </div>
              )}

              {isCorporalOuMoral && (
                <div className="form-group">
                  <label className="form-label">Especifique o dano</label>
                  <input className="form-input" value={novo.dano_especificacao} onChange={e => setC('dano_especificacao', e.target.value)} />
                </div>
              )}

              <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="form-select" value={novo.status} onChange={e => setC('status', e.target.value)}>
                    {statusOptions.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Observações</label>
                <textarea className="form-input" rows={2} style={{ resize: 'vertical' }} value={novo.observacoes} onChange={e => setC('observacoes', e.target.value)} />
              </div>
            </div>

            {novo.tipo_dano && (
              <div className="card" style={{ paddingTop: 14, paddingBottom: 16 }}>
                {sec('Ocorrência')}
                <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr' }}>
                  <div className="form-group">
                    <label className="form-label">Data do Aviso</label>
                    <input className="form-input" type="date" value={novo.data_aviso} onChange={e => setC('data_aviso', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Data da Ocorrência</label>
                    <input className="form-input" type="date" value={novo.data_ocorrencia} onChange={e => setC('data_ocorrencia', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Hora da Ocorrência</label>
                    <input className="form-input" type="time" value={novo.hora_ocorrencia} onChange={e => setC('hora_ocorrencia', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Tipo de Sinistro</label>
                    <select className="form-select" value={novo.tipo_sinistro} onChange={e => setC('tipo_sinistro', e.target.value)}>
                      <option value="">Selecione...</option>
                      {tiposSinistroTerceiro.map(x => <option key={x}>{x}</option>)}
                    </select>
                  </div>
                </div>
                {isVeiculo && (
                  <div className="form-group">
                    <label className="form-label">Local do Fato</label>
                    <input className="form-input" value={novo.local_fato} onChange={e => setC('local_fato', e.target.value)} />
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Descrição do Ocorrido</label>
                  <textarea className="form-input" rows={2} style={{ resize: 'vertical' }} value={novo.descricao} onChange={e => setC('descricao', e.target.value)} />
                </div>
              </div>
            )}

            {isVeiculo && (
              <div className="card" style={{ paddingTop: 14, paddingBottom: 16 }}>
                {sec('Condutor no momento do sinistro')}
                <div className="form-grid" style={{ gridTemplateColumns: '2fr 1fr 1fr' }}>
                  <div className="form-group">
                    <label className="form-label">Nome</label>
                    <input className="form-input" value={novo.condutor_nome} onChange={e => setC('condutor_nome', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">CPF</label>
                    <input className="form-input" value={novo.condutor_cpf} onChange={e => setC('condutor_cpf', maskCpf(e.target.value))} placeholder="000.000.000-00" maxLength={14} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Nascimento</label>
                    <input className="form-input" type="date" value={novo.condutor_nascimento} onChange={e => setC('condutor_nascimento', e.target.value)} />
                  </div>
                </div>
                <div className="form-grid" style={{ gridTemplateColumns: '1fr 2fr 1fr 1fr' }}>
                  <div className="form-group">
                    <label className="form-label">CEP</label>
                    <input className="form-input" value={novo.condutor_cep} onChange={e => { const v = maskCEP(e.target.value); setC('condutor_cep', v); if (v.length === 9) buscarCepCondutorTerceiro(v) }} placeholder="00000-000" maxLength={9} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Rua</label>
                    <input className="form-input" value={novo.condutor_endereco} onChange={e => setC('condutor_endereco', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Número</label>
                    <input className="form-input" value={novo.condutor_numero} onChange={e => setC('condutor_numero', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Complemento</label>
                    <input className="form-input" value={novo.condutor_complemento} onChange={e => setC('condutor_complemento', e.target.value)} />
                  </div>
                </div>
                <div className="form-grid" style={{ gridTemplateColumns: '2fr 1fr 1fr' }}>
                  <div className="form-group">
                    <label className="form-label">Bairro</label>
                    <input className="form-input" value={novo.condutor_bairro} onChange={e => setC('condutor_bairro', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Cidade</label>
                    <input className="form-input" value={novo.condutor_cidade} onChange={e => setC('condutor_cidade', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Estado</label>
                    <input className="form-input" value={novo.condutor_estado} onChange={e => setC('condutor_estado', e.target.value.toUpperCase().slice(0, 2))} maxLength={2} />
                  </div>
                </div>
              </div>
            )}

            <div className="card" style={{ paddingTop: 14, paddingBottom: 16 }}>
              {sec('Valores')}
              <div className="form-group">
                <label className="form-label">Valor do Conserto/Indenização</label>
                <input className="form-input" value={novo.valor_indenizacao}
                  onChange={e => setC('valor_indenizacao', e.target.value)}
                  onBlur={e => setC('valor_indenizacao', e.target.value.trim() === '' ? '' : formatMoney(parseMoneyLocal(e.target.value)))}
                  placeholder="R$ 0,00" style={{ maxWidth: 260 }} />
              </div>
            </div>

            {isVeiculo && (
              <div className="card" style={{ paddingTop: 14, paddingBottom: 16 }}>
                {sec('Oficina')}
                <div className="form-grid" style={{ gridTemplateColumns: '2fr 1fr 1fr' }}>
                  <div className="form-group">
                    <label className="form-label">Nome</label>
                    <OficinaPicker oficinas={oficinas} oficinaId={novo.oficina_id} onSelecionar={id => setC('oficina_id', id)} onOficinaCriada={onOficinaCriada} mostrarResumo={false} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Telefone</label>
                    <input className="form-input" value={oficinas.find(o => String(o.id) === String(novo.oficina_id))?.telefone || ''} disabled placeholder="—" style={{ background: '#f8f9fa', color: '#666' }} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Responsável</label>
                    <input className="form-input" value={oficinas.find(o => String(o.id) === String(novo.oficina_id))?.responsavel || ''} disabled placeholder="—" style={{ background: '#f8f9fa', color: '#666' }} />
                  </div>
                </div>
              </div>
            )}

            <div className="card" style={{ paddingTop: 14, paddingBottom: 16 }}>
              {sec('Anexos')}
              <AnexosSinistro id={terceiroId} basePath="/sinistros/terceiros" />
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-danger btn-icon" title="Excluir" onClick={excluir}><IconExcluir size={16} /></button>
              <button type="button" className="btn btn-secondary" onClick={cancelarEdicao}>Cancelar</button>
              <button type="button" className="btn btn-primary" onClick={salvar} disabled={salvando}>{salvando ? 'Salvando...' : 'Salvar'}</button>
            </div>
          </div>

          <div style={{ width: 450, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div className="card">
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
                Histórico de Atendimento
              </div>
              <HistoricoTerceiro sinistroId={t.sinistro_id} terceiroId={terceiroId} />
            </div>
          </div>
        </div>
      )}

      {!modoEdicao && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' }}>
          <div className="card">
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
              Histórico de Atendimento
            </div>
            <HistoricoTerceiro sinistroId={t.sinistro_id} terceiroId={terceiroId} />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 20, minWidth: 0 }}>
            <div className="card" style={{ paddingTop: 14, paddingBottom: 16 }}>
              {sec('Resumo do Sinistro do Terceiro')}

              {subLabelSublinhado('Dados')}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, fontSize: 13 }}>
                <CampoResumo label="Nome" valor={t.nome} />
                <CampoResumo label="CPF/CNPJ" valor={t.cpf_cnpj} />
                <CampoResumo label="Telefone" valor={t.telefone} />
              </div>
              <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, fontSize: 13 }}>
                <CampoResumo label="Nº do Sinistro" valor={t.numero_sinistro} />
                <CampoResumo label="Tipo de Dano" valor={t.tipo_dano ? `${t.tipo_dano}${t.subtipo_dano ? ` (${t.subtipo_dano})` : ''}` : null} />
              </div>
              {isVeiculo && (
                <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 10, fontSize: 13 }}>
                  <CampoResumo label="Modelo do Veículo" valor={t.veiculo_modelo} />
                  <CampoResumo label="Ano/Modelo" valor={t.veiculo_ano_modelo} />
                  <CampoResumo label="Placa" valor={t.veiculo_placa} />
                  <CampoResumo label="Chassi" valor={t.veiculo_chassi} />
                </div>
              )}
              {isResidencia && (
                <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10, fontSize: 13 }}>
                  <CampoResumo label="Endereço" valor={[t.endereco_rua, t.endereco_numero].filter(Boolean).join(', ') || null} />
                  <CampoResumo label="Bairro" valor={t.endereco_bairro} />
                  <CampoResumo label="Cidade/UF" valor={[t.endereco_cidade, t.endereco_estado].filter(Boolean).join('/') || null} />
                </div>
              )}
              {isObjeto && <div style={{ marginTop: 10 }}><CampoResumo label="Objeto" valor={t.objeto_especificacao} /></div>}
              {isCorporalOuMoral && <div style={{ marginTop: 10 }}><CampoResumo label="Dano" valor={t.dano_especificacao} /></div>}
              {t.observacoes && <div style={{ marginTop: 10 }}><CampoResumo label="Observações" valor={t.observacoes} /></div>}

              {t.tipo_dano && (<>
                {subLabelSublinhado('Ocorrência')}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, fontSize: 13 }}>
                  <CampoResumo label="Data do Aviso" valor={fmtData(t.data_aviso)} />
                  <CampoResumo label="Data da Ocorrência" valor={fmtData(t.data_ocorrencia)} />
                  <CampoResumo label="Hora da Ocorrência" valor={t.hora_ocorrencia} />
                  <CampoResumo label="Tipo de Sinistro" valor={t.tipo_sinistro} />
                </div>
                <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13 }}>
                  {isVeiculo && <CampoResumo label="Local do Fato" valor={t.local_fato} />}
                  <CampoResumo label="Descrição do Ocorrido" valor={t.descricao} />
                </div>
              </>)}

              {isVeiculo && (<>
                {subLabelSublinhado('Condutor no momento do sinistro')}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, fontSize: 13 }}>
                  <CampoResumo label="Nome" valor={t.condutor_nome} />
                  <CampoResumo label="CPF" valor={t.condutor_cpf} />
                  <CampoResumo label="Nascimento" valor={fmtData(t.condutor_nascimento)} />
                  <CampoResumo label="Endereço" valor={[t.condutor_endereco, t.condutor_numero].filter(Boolean).join(', ') || null} />
                  <CampoResumo label="Bairro" valor={t.condutor_bairro} />
                  <CampoResumo label="Cidade/UF" valor={[t.condutor_cidade, t.condutor_estado].filter(Boolean).join('/') || null} />
                </div>
              </>)}

              {subLabelSublinhado('Valores')}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 13 }}>
                <CampoResumo label="Valor do Conserto/Indenização" valor={t.valor_indenizacao != null ? formatMoney(t.valor_indenizacao) : null} />
              </div>

              {isVeiculo && (<>
                {subLabelSublinhado('Oficina')}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, fontSize: 13 }}>
                  <CampoResumo label="Nome" valor={oficinaObj?.nome || t.oficina_nome} />
                  <CampoResumo label="Telefone" valor={oficinaObj?.telefone || t.oficina_telefone} />
                  <CampoResumo label="Responsável" valor={oficinaObj?.responsavel || t.oficina_responsavel} />
                </div>
              </>)}
            </div>

            <div className="card" style={{ paddingTop: 14, paddingBottom: 16 }}>
              {sec('Anexos')}
              <AnexosSinistro id={terceiroId} basePath="/sinistros/terceiros" />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ===== Formulário/modal do Sinistro =====
function SinistroModal({ sinistroId: sinistroIdInicial, apoliceInicial, onClose, onSaved, voltarLabel = 'Sinistros', onVoltar = null, onVerTerceiro }) {
  const [sinistroId, setSinistroId] = useState(sinistroIdInicial || null)
  const [form, setForm] = useState(initForm)
  const [apoliceBusca, setApoliceBusca] = useState('')
  const [apoliceSelecionada, setApoliceSelecionada] = useState(null)
  const [mostrarSugestoesApolice, setMostrarSugestoesApolice] = useState(false)
  const [apolicesTodas, setApolicesTodas] = useState([])
  const [itensApolice, setItensApolice] = useState([])
  const [oficinas, setOficinas] = useState([])
  const [clienteSelecionado, setClienteSelecionado] = useState(null)
  const [anexoApolicePdf, setAnexoApolicePdf] = useState(null)
  const [salvando, setSalvando] = useState(false)
  const [carregando, setCarregando] = useState(!!sinistroIdInicial)
  // Sinistro ja salvo abre direto na visualizacao (resumo); so entra no formulario de edicao
  // completo ao criar um novo ou ao clicar em "Editar".
  const [modoEdicao, setModoEdicao] = useState(!sinistroIdInicial)
  const [pedirSenhaMasterExcluir, setPedirSenhaMasterExcluir] = useState(false)
  const apoliceTravada = !!apoliceInicial

  useEffect(() => {
    api.get('/apolices').then(r => setApolicesTodas(Array.isArray(r.data) ? r.data : [])).catch(() => setApolicesTodas([]))
    api.get('/oficinas').then(r => setOficinas(Array.isArray(r.data) ? r.data : [])).catch(() => setOficinas([]))
  }, [])
  const onOficinaCriada = (nova) => setOficinas(prev => [...prev, nova])

  // Barra lateral: dados do cliente (telefone/e-mail nao vem na listagem de apolices) e o PDF da
  // apolice anexado (pra abrir com 1 clique), buscados assim que a apolice/cliente e escolhida.
  useEffect(() => {
    if (!form.cliente_id) { setClienteSelecionado(null); return }
    api.get(`/clientes/${form.cliente_id}`).then(r => setClienteSelecionado(r.data)).catch(() => setClienteSelecionado(null))
  }, [form.cliente_id])

  useEffect(() => {
    if (!form.apolice_id) { setAnexoApolicePdf(null); return }
    api.get(`/apolices/${form.apolice_id}/anexos`).then(r => {
      const lista = Array.isArray(r.data) ? r.data : []
      setAnexoApolicePdf(lista.find(a => a.tipo === 'Apólice') || lista.find(a => a.tipo === 'Endosso') || lista[0] || null)
    }).catch(() => setAnexoApolicePdf(null))
  }, [form.apolice_id])

  const abrirPdfApolice = async () => {
    if (!anexoApolicePdf) return
    try {
      const { data } = await api.get(`/apolices/anexos/${anexoApolicePdf.id}/dados`)
      const parts = data.dados.split(',')
      const mimeType = parts[0].split(':')[1].split(';')[0]
      const byteStr = atob(parts[1])
      const ab = new ArrayBuffer(byteStr.length)
      const ia = new Uint8Array(ab)
      for (let x = 0; x < byteStr.length; x++) ia[x] = byteStr.charCodeAt(x)
      const blob = new Blob([ab], { type: mimeType })
      window.open(URL.createObjectURL(blob), '_blank')
    } catch (err) {
      avisar('Erro ao abrir PDF: ' + (err.response?.data?.erro || err.message))
    }
  }

  useEffect(() => {
    if (apoliceInicial) {
      setApoliceSelecionada(apoliceInicial)
      setApoliceBusca(`${apoliceInicial.cliente_nome} — ${apoliceInicial.numero_apolice || apoliceInicial.emissao_numero_apolice || 'sem número'}`)
      setForm(f => ({ ...f, apolice_id: apoliceInicial.id, cliente_id: apoliceInicial.cliente_id }))
    }
  }, [apoliceInicial])

  const carregarSinistro = async (id) => {
    const { data: s } = await api.get(`/sinistros/${id}`)
    setForm({
      numero_sinistro: s.numero_sinistro || '',
      apolice_id: s.apolice_id, cliente_id: s.cliente_id, apolice_item_id: s.apolice_item_id || '',
      data_aviso: s.data_aviso?.split('T')[0] || '', data_ocorrencia: s.data_ocorrencia?.split('T')[0] || '',
      hora_ocorrencia: s.hora_ocorrencia || '', local_fato: s.local_fato || '', descricao: s.descricao || '',
      condutor_nome: s.condutor_nome || '', condutor_cpf: s.condutor_cpf || '',
      condutor_nascimento: s.condutor_nascimento?.split('T')[0] || '',
      condutor_cep: s.condutor_cep || '', condutor_endereco: s.condutor_endereco || '',
      condutor_numero: s.condutor_numero || '', condutor_complemento: s.condutor_complemento || '',
      condutor_bairro: s.condutor_bairro || '', condutor_cidade: s.condutor_cidade || '', condutor_estado: s.condutor_estado || '',
      tipo_sinistro: s.tipo_sinistro || '',
      valor_franquia: s.valor_franquia != null ? formatMoney(s.valor_franquia) : '',
      valor_indenizacao: s.valor_indenizacao != null ? formatMoney(s.valor_indenizacao) : '',
      valor_conserto: s.valor_conserto != null ? formatMoney(s.valor_conserto) : '',
      oficina_id: s.oficina_id || '',
      status: s.status || 'aberto', tem_terceiros: !!s.tem_terceiros, observacoes: s.observacoes || '',
    })
    setApoliceSelecionada({ id: s.apolice_id, cliente_id: s.cliente_id, cliente_nome: s.cliente_nome, numero_apolice: s.numero_apolice, emissao_numero_apolice: s.emissao_numero_apolice, seguradora: s.seguradora, ramo: s.ramo })
    setApoliceBusca(`${s.cliente_nome} — ${s.numero_apolice || s.emissao_numero_apolice || 'sem número'}`)
  }

  useEffect(() => {
    if (!sinistroIdInicial) return
    setCarregando(true)
    carregarSinistro(sinistroIdInicial).catch(() => {}).finally(() => setCarregando(false))
  }, [sinistroIdInicial])

  // Cancelar a edicao de um sinistro ja existente descarta as alteracoes locais recarregando
  // os dados originais do servidor antes de voltar pra visualizacao (resumo).
  const cancelarEdicao = async () => {
    if (!sinistroId) { onClose(); return }
    setCarregando(true)
    try { await carregarSinistro(sinistroId) } catch { /* mantem os dados atuais se falhar */ }
    setCarregando(false)
    setModoEdicao(false)
  }

  useEffect(() => {
    if (!form.apolice_id) { setItensApolice([]); return }
    api.get(`/itens/apolice/${form.apolice_id}`).then(r => setItensApolice(Array.isArray(r.data) ? r.data : [])).catch(() => setItensApolice([]))
  }, [form.apolice_id])

  const itemSelecionadoObj = itensApolice.find(i => String(i.id) === String(form.apolice_item_id)) || null
  const oficinaSelecionadaObj = oficinas.find(o => String(o.id) === String(form.oficina_id)) || null

  const apolicesFiltradas = (() => {
    const termo = apoliceBusca.trim().toLowerCase()
    if (!termo || apoliceTravada) return []
    return apolicesTodas
      .filter(a => a.status === 'emitido')
      .filter(a => (a.cliente_nome || '').toLowerCase().includes(termo) || (a.numero_apolice || '').includes(termo) || (a.emissao_numero_apolice || '').includes(termo))
      .slice(0, 8)
  })()

  const selecionarApolice = (a) => {
    setApoliceSelecionada(a)
    setApoliceBusca(`${a.cliente_nome} — ${a.numero_apolice || a.emissao_numero_apolice || 'sem número'}`)
    setForm(f => ({ ...f, apolice_id: a.id, cliente_id: a.cliente_id, apolice_item_id: '' }))
    setMostrarSugestoesApolice(false)
  }
  const navApolice = useNavegacaoLista(apolicesFiltradas.length, (i) => selecionarApolice(apolicesFiltradas[i]))

  const setField = (campo, valor) => setForm(f => ({ ...f, [campo]: valor }))

  const buscarCepCondutor = async (cep) => {
    const raw = cep.replace(/\D/g, '')
    if (raw.length !== 8) return
    try {
      const res = await fetch(`https://viacep.com.br/ws/${raw}/json/`)
      const data = await res.json()
      if (!data.erro) setForm(f => ({ ...f, condutor_endereco: data.logradouro, condutor_bairro: data.bairro, condutor_cidade: data.localidade, condutor_estado: data.uf }))
    } catch { /* CEP nao encontrado, deixa o usuario preencher manualmente */ }
  }

  // CNPJ nao cabe no campo CPF do condutor (segurado PJ nao dirige), entao so copia CPF.
  const copiarCondutorDoSegurado = () => {
    const c = clienteSelecionado
    if (!c) return
    const cpf = (c.cpf_cnpj || '').replace(/\D/g, '').length === 11 ? maskCpf(c.cpf_cnpj) : ''
    setForm(f => ({
      ...f,
      condutor_nome: c.nome || '', condutor_cpf: cpf,
      condutor_nascimento: c.data_nascimento?.split('T')[0] || '',
      condutor_cep: c.cep || '', condutor_endereco: c.endereco || '', condutor_numero: c.numero || '',
      condutor_complemento: c.complemento || '', condutor_bairro: c.bairro || '',
      condutor_cidade: c.cidade || '', condutor_estado: c.estado || '',
    }))
  }

  const salvar = async () => {
    if (!form.apolice_id) { avisar('Selecione a apólice do sinistro.'); return null }
    setSalvando(true)
    try {
      const payload = { ...form, valor_franquia: form.valor_franquia, valor_indenizacao: form.valor_indenizacao, valor_conserto: form.valor_conserto }
      const { data } = sinistroId ? await api.put(`/sinistros/${sinistroId}`, payload) : await api.post('/sinistros', payload)
      setSinistroId(data.id)
      onSaved?.()
      setModoEdicao(false)
      return data.id
    } catch (err) {
      avisar('Erro ao salvar sinistro: ' + (err.response?.data?.erro || err.message))
      return null
    } finally {
      setSalvando(false)
    }
  }

  const excluir = async () => {
    if (!(await confirmar('Excluir este sinistro? Essa ação não pode ser desfeita.'))) return
    setPedirSenhaMasterExcluir(true)
  }
  const confirmarExclusaoComSenha = async (senha) => {
    await api.delete(`/sinistros/${sinistroId}`, { data: { senha_master: senha } })
    setPedirSenhaMasterExcluir(false)
    onSaved?.()
    onClose()
  }

  if (carregando) return <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Carregando...</div>

  const st = statusInfo(form.status)

  // Reaproveitado tanto na barra lateral do formulario de edicao quanto na coluna direita da
  // visualizacao (resumo) pos-criacao.
  const resumoApoliceCard = apoliceSelecionada && (
    <div className="card" style={{ border: '2px solid rgba(var(--primary-rgb),0.15)' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
        Resumo da Apólice
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <CampoResumo label="Segurado" valor={apoliceSelecionada.cliente_nome} />
          <CampoResumo label="CPF/CNPJ" valor={clienteSelecionado?.cpf_cnpj} />
          <CampoResumo label="Nº Apólice" valor={apoliceSelecionada.numero_apolice || apoliceSelecionada.emissao_numero_apolice} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <CampoResumo label="Item" valor={itemSelecionadoObj ? (itemSelecionadoObj.descricao_item || itemSelecionadoObj.item_modelo || `Item ${itemSelecionadoObj.numero_item}`) : null} />
          <CampoResumo label="Placa" valor={itemSelecionadoObj?.item_placa} />
          <CampoResumo label="Chassi" valor={itemSelecionadoObj?.item_chassi} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <div style={{ fontSize: 11, color: '#999', marginBottom: 2 }}>Telefone{clienteSelecionado?.telefones?.length > 1 ? 's' : ''}</div>
            {clienteSelecionado?.telefones?.length ? clienteSelecionado.telefones.map((t, i) => (
              <div key={i} style={{ color: '#333' }}>{t.numero}{t.nome ? ` (${t.nome})` : ''}</div>
            )) : <div style={{ color: '#333' }}>—</div>}
          </div>
          <div>
            <div style={{ fontSize: 11, color: '#999', marginBottom: 2 }}>E-mail{clienteSelecionado?.emails?.length > 1 ? 's' : ''}</div>
            {clienteSelecionado?.emails?.length ? clienteSelecionado.emails.map((e, i) => (
              <div key={i} style={{ color: '#333', wordBreak: 'break-all' }}>{e}</div>
            )) : <div style={{ color: '#333' }}>—</div>}
          </div>
        </div>
      </div>
      {anexoApolicePdf && (
        <button type="button" className="btn btn-secondary" style={{ width: '100%', marginTop: 12 }} onClick={abrirPdfApolice}>
          📄 Abrir PDF da Apólice
        </button>
      )}
    </div>
  )

  const historicoCard = (
    <div className="card">
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
        Histórico de Atendimento
      </div>
      {sinistroId
        ? <HistoricoSinistro sinistroId={sinistroId} />
        : <div style={{ padding: 14, textAlign: 'center', color: '#aaa', background: '#f8f9fa', borderRadius: 8, fontSize: 12 }}>Disponível depois de salvar o sinistro.</div>}
    </div>
  )

  return (
    <div style={{ padding: '24px 24px 40px' }}>
      <div style={{ marginBottom: 14 }}>
        <button type="button" onClick={onVoltar || onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', fontSize: 13, fontWeight: 600, padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
          ‹ Voltar para {voltarLabel}
        </button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <h2 style={{ margin: '8px 0 0', fontSize: 22, fontWeight: 700 }}>{sinistroId ? `Sinistro ${form.numero_sinistro ? `nº ${form.numero_sinistro}` : ''}` : 'Novo Sinistro'}</h2>
          {sinistroId && !modoEdicao && (
            <div style={{ display: 'flex', gap: 10, marginTop: 8, flexShrink: 0 }}>
              <button type="button" className="btn btn-danger btn-icon" title="Excluir" onClick={excluir}><IconExcluir size={16} /></button>
              <button type="button" className="btn btn-primary btn-icon" title="Editar" onClick={() => setModoEdicao(true)}><IconEditar size={16} /></button>
            </div>
          )}
        </div>
      </div>

      {apoliceSelecionada && (
        <div className="card" style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 22, fontWeight: 700, color: 'var(--primary)', letterSpacing: '0.01em' }}>{apoliceSelecionada.cliente_nome}</span>
          <span className={`badge ${st.cls}`}>{st.label}</span>
        </div>
      )}

      {modoEdicao && (
      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 20, minWidth: 0 }}>

        {/* ===== CARD DADOS ===== */}
        <div className="card" style={{ paddingTop: 14, paddingBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, paddingBottom: 6, borderBottom: '2px solid var(--primary-light)' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Dados</div>
            <span className={`badge ${st.cls}`}>{st.label}</span>
          </div>

          <div className="form-group">
            <label className="form-label">Apólice *</label>
            <div style={{ position: 'relative' }}>
              <input className="form-input" value={apoliceBusca}
                onChange={e => { setApoliceBusca(e.target.value); setMostrarSugestoesApolice(true); if (!e.target.value) { setApoliceSelecionada(null); setField('apolice_id', ''); setField('cliente_id', '') }; navApolice.reset() }}
                onFocus={() => setMostrarSugestoesApolice(true)}
                onBlur={() => setTimeout(() => setMostrarSugestoesApolice(false), 150)}
                onKeyDown={navApolice.onKeyDown}
                placeholder="Buscar por nome do cliente ou nº da apólice..."
                disabled={apoliceTravada}
                style={{ opacity: apoliceTravada ? 0.7 : 1 }}
                autoComplete="off" />
              {mostrarSugestoesApolice && apolicesFiltradas.length > 0 && (
                <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 20, background: '#fff', border: '1px solid #D9DDE3', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto' }}>
                  {apolicesFiltradas.map((a, i) => (
                    <button key={a.id} type="button" onMouseDown={() => selecionarApolice(a)}
                      onMouseEnter={() => navApolice.setIndice(i)}
                      style={{ width: '100%', textAlign: 'left', border: 'none', background: i === navApolice.indice ? 'rgba(var(--primary-rgb),0.08)' : '#fff', padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #F1F3F5' }}>
                      <div style={{ fontSize: 13, color: '#1A1A2E', fontWeight: 600 }}>{a.cliente_nome}</div>
                      <div style={{ fontSize: 11, color: '#7A7F87', marginTop: 2 }}>{a.seguradora} · {a.ramo} · Nº {a.numero_apolice || a.emissao_numero_apolice}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {apoliceSelecionada && <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>{apoliceSelecionada.seguradora} · {apoliceSelecionada.ramo}</div>}
          </div>

          <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr' }}>
            <div className="form-group">
              <label className="form-label">Nº do Sinistro</label>
              <input className="form-input" value={form.numero_sinistro} onChange={e => setField('numero_sinistro', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Item Segurado</label>
              <select className="form-select" value={form.apolice_item_id} onChange={e => {
                const itemId = e.target.value
                const item = itensApolice.find(i => String(i.id) === String(itemId))
                const franquia = franquiaDoItem(item)
                setForm(f => ({ ...f, apolice_item_id: itemId, valor_franquia: franquia || f.valor_franquia }))
              }} disabled={itensApolice.length === 0}>
                <option value="">{itensApolice.length === 0 ? 'Sem itens nesta apólice' : 'Selecione...'}</option>
                {itensApolice.map(i => <option key={i.id} value={i.id}>{i.item_placa || i.descricao_item || `Item ${i.numero_item}`}{i.item_modelo ? ` — ${i.item_modelo}` : ''}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-select" value={form.status} onChange={e => setField('status', e.target.value)}>
                {statusOptions.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Houve terceiros envolvidos?</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {[['sim', true], ['não', false]].map(([lbl, val]) => (
                  <button key={lbl} type="button"
                    className={`btn btn-sm ${form.tem_terceiros === val ? 'btn-primary' : 'btn-secondary'}`}
                    style={{ flex: 1, textTransform: 'capitalize' }}
                    onClick={() => setField('tem_terceiros', val)}>{lbl}</button>
                ))}
              </div>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Observações</label>
            <textarea className="form-input" rows={2} style={{ resize: 'vertical' }} value={form.observacoes} onChange={e => setField('observacoes', e.target.value)} />
          </div>
        </div>

        {/* ===== CARD OCORRÊNCIA ===== */}
        <div className="card" style={{ paddingTop: 14, paddingBottom: 16 }}>
          {sec('Ocorrência')}
          <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr' }}>
              <div className="form-group">
                <label className="form-label">Data do Aviso</label>
                <input className="form-input" type="date" value={form.data_aviso} onChange={e => setField('data_aviso', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Data da Ocorrência</label>
                <input className="form-input" type="date" value={form.data_ocorrencia} onChange={e => setField('data_ocorrencia', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Hora da Ocorrência</label>
                <input className="form-input" type="time" value={form.hora_ocorrencia} onChange={e => setField('hora_ocorrencia', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Tipo de Sinistro</label>
                <select className="form-select" value={form.tipo_sinistro} onChange={e => setField('tipo_sinistro', e.target.value)}>
                  <option value="">Selecione...</option>
                  {tiposSinistro.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Local do Fato</label>
              <input className="form-input" value={form.local_fato} onChange={e => setField('local_fato', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Descrição do Ocorrido</label>
              <textarea className="form-input" rows={3} style={{ resize: 'vertical' }} value={form.descricao} onChange={e => setField('descricao', e.target.value)} />
            </div>
        </div>

        {/* ===== CARD CONDUTOR ===== */}
        <div className="card" style={{ paddingTop: 14, paddingBottom: 16, position: 'relative' }}>
          {sec('Condutor no momento do sinistro')}
          <button type="button" className="btn btn-secondary btn-sm" onClick={copiarCondutorDoSegurado} disabled={!clienteSelecionado}
            title={clienteSelecionado ? 'Preencher com os dados do segurado' : 'Selecione a apólice do sinistro primeiro'}
            style={{ position: 'absolute', top: 10, right: 16 }}>
            Copiar do Segurado
          </button>
          <div className="form-grid" style={{ gridTemplateColumns: '2fr 1fr 1fr' }}>
            <div className="form-group">
              <label className="form-label">Nome</label>
              <input className="form-input" value={form.condutor_nome} onChange={e => setField('condutor_nome', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">CPF</label>
              <input className="form-input" value={form.condutor_cpf} onChange={e => setField('condutor_cpf', maskCpf(e.target.value))} placeholder="000.000.000-00" maxLength={14} />
            </div>
            <div className="form-group">
              <label className="form-label">Nascimento</label>
              <input className="form-input" type="date" value={form.condutor_nascimento} onChange={e => setField('condutor_nascimento', e.target.value)} />
            </div>
          </div>
          <div className="form-grid" style={{ gridTemplateColumns: '1fr 2fr 1fr 1fr' }}>
            <div className="form-group">
              <label className="form-label">CEP</label>
              <input className="form-input" value={form.condutor_cep} onChange={e => { const v = maskCEP(e.target.value); setField('condutor_cep', v); if (v.length === 9) buscarCepCondutor(v) }} placeholder="00000-000" maxLength={9} />
            </div>
            <div className="form-group">
              <label className="form-label">Rua</label>
              <input className="form-input" value={form.condutor_endereco} onChange={e => setField('condutor_endereco', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Número</label>
              <input className="form-input" value={form.condutor_numero} onChange={e => setField('condutor_numero', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Complemento</label>
              <input className="form-input" value={form.condutor_complemento} onChange={e => setField('condutor_complemento', e.target.value)} />
            </div>
          </div>
          <div className="form-grid" style={{ gridTemplateColumns: '2fr 1fr 1fr' }}>
            <div className="form-group">
              <label className="form-label">Bairro</label>
              <input className="form-input" value={form.condutor_bairro} onChange={e => setField('condutor_bairro', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Cidade</label>
              <input className="form-input" value={form.condutor_cidade} onChange={e => setField('condutor_cidade', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Estado</label>
              <input className="form-input" value={form.condutor_estado} onChange={e => setField('condutor_estado', e.target.value.toUpperCase().slice(0, 2))} maxLength={2} />
            </div>
          </div>
        </div>

        {/* ===== CARD VALORES ===== */}
        <div className="card" style={{ paddingTop: 14, paddingBottom: 16 }}>
          {sec('Valores')}
          <div className="form-grid" style={{ gridTemplateColumns: form.tipo_sinistro === 'Danos a Terceiros' ? '1fr' : '1fr 1fr' }}>
            <div className="form-group">
              <label className="form-label">Valor da Franquia</label>
              <input className="form-input" value={form.valor_franquia} onChange={e => setField('valor_franquia', e.target.value)} placeholder="R$ 0,00" />
            </div>
            {form.tipo_sinistro === 'Perda Parcial' && (
              <div className="form-group">
                <label className="form-label">Valor do Conserto</label>
                <input className="form-input" value={form.valor_conserto} onChange={e => setField('valor_conserto', e.target.value)} placeholder="R$ 0,00" />
              </div>
            )}
            {(form.tipo_sinistro === 'Perda Total (Colisão)' || form.tipo_sinistro === 'Perda Total (Roubo)') && (
              <div className="form-group">
                <label className="form-label">Valor da Indenização</label>
                <input className="form-input" value={form.valor_indenizacao} onChange={e => setField('valor_indenizacao', e.target.value)} placeholder="R$ 0,00" />
              </div>
            )}
            {!form.tipo_sinistro && (
              <div className="form-group">
                <label className="form-label">Valor do Conserto / Indenização</label>
                <div style={{ padding: '9px 12px', fontSize: 12, color: '#aaa', background: '#f8f9fa', borderRadius: 8 }}>Escolha o Tipo de Sinistro acima</div>
              </div>
            )}
          </div>
        </div>

        {/* ===== CARD OFICINA ===== */}
        <div className="card" style={{ paddingTop: 14, paddingBottom: 16 }}>
          {sec('Oficina')}
          <div className="form-grid" style={{ gridTemplateColumns: '2fr 1fr 1fr' }}>
            <div className="form-group">
              <label className="form-label">Nome</label>
              <OficinaPicker oficinas={oficinas} oficinaId={form.oficina_id} onSelecionar={id => setField('oficina_id', id)} onOficinaCriada={onOficinaCriada} mostrarResumo={false} />
            </div>
            <div className="form-group">
              <label className="form-label">Telefone</label>
              <input className="form-input" value={oficinaSelecionadaObj?.telefone || ''} disabled placeholder="—" style={{ background: '#f8f9fa', color: '#666' }} />
            </div>
            <div className="form-group">
              <label className="form-label">Responsável</label>
              <input className="form-input" value={oficinaSelecionadaObj?.responsavel || ''} disabled placeholder="—" style={{ background: '#f8f9fa', color: '#666' }} />
            </div>
          </div>
        </div>

        {/* ===== CARD ANEXOS ===== */}
        <div className="card" style={{ paddingTop: 14, paddingBottom: 16 }}>
          {sec('Anexos')}
          {sinistroId
            ? <AnexosSinistro id={sinistroId} basePath="/sinistros" />
            : <div style={{ padding: 24, textAlign: 'center', color: '#aaa', background: '#f8f9fa', borderRadius: 8, fontSize: 13 }}>Salve os dados do sinistro primeiro.</div>}
        </div>

        {/* ===== CARD TERCEIROS ===== */}
        <div className="card" style={{ paddingTop: 14, paddingBottom: 16 }}>
          {sec('Terceiros')}
          {sinistroId
            ? <TerceirosSinistro sinistroId={sinistroId} temTerceiros={form.tem_terceiros} oficinas={oficinas} onOficinaCriada={onOficinaCriada} onVer={onVerTerceiro} />
            : <div style={{ padding: 24, textAlign: 'center', color: '#aaa', background: '#f8f9fa', borderRadius: 8, fontSize: 13 }}>Salve os dados do sinistro primeiro.</div>}
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          {sinistroId && <button type="button" className="btn btn-danger btn-icon" title="Excluir" onClick={excluir}><IconExcluir size={16} /></button>}
          <button type="button" className="btn btn-secondary" onClick={cancelarEdicao}>{sinistroId ? 'Cancelar' : 'Fechar'}</button>
          <button type="button" className="btn btn-primary" onClick={salvar} disabled={salvando}>{salvando ? 'Salvando...' : 'Salvar'}</button>
        </div>
      </div>

      {/* ===== BARRA LATERAL DIREITA ===== */}
      <div style={{ width: 450, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 20 }}>
        {resumoApoliceCard}
        {historicoCard}
      </div>
      </div>
      )}

      {/* ===== VISUALIZACAO (sinistro ja salvo, fora do modo de edicao) ===== */}
      {sinistroId && !modoEdicao && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' }}>
          {historicoCard}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 20, minWidth: 0 }}>
            {resumoApoliceCard}

            {/* ===== CARD RESUMO DO SINISTRO ===== */}
            <div className="card" style={{ paddingTop: 14, paddingBottom: 16 }}>
              {sec('Resumo do Sinistro')}

              {subLabelSublinhado('Dados')}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, fontSize: 13 }}>
                <CampoResumo label="Nº do Sinistro" valor={form.numero_sinistro} />
                <CampoResumo label="Item Segurado" valor={itemSelecionadoObj ? (itemSelecionadoObj.item_placa || itemSelecionadoObj.descricao_item || `Item ${itemSelecionadoObj.numero_item}`) : null} />
                <CampoResumo label="Houve terceiros envolvidos?" valor={form.tem_terceiros ? 'Sim' : 'Não'} />
              </div>
              {form.observacoes && <div style={{ marginTop: 10 }}><CampoResumo label="Observações" valor={form.observacoes} /></div>}

              {subLabelSublinhado('Ocorrência')}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, fontSize: 13 }}>
                <CampoResumo label="Data do Aviso" valor={fmtData(form.data_aviso)} />
                <CampoResumo label="Data da Ocorrência" valor={fmtData(form.data_ocorrencia)} />
                <CampoResumo label="Hora da Ocorrência" valor={form.hora_ocorrencia} />
                <CampoResumo label="Tipo de Sinistro" valor={form.tipo_sinistro} />
              </div>
              <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13 }}>
                <CampoResumo label="Local do Fato" valor={form.local_fato} />
                <CampoResumo label="Descrição do Ocorrido" valor={form.descricao} />
              </div>

              {subLabelSublinhado('Condutor no momento do sinistro')}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, fontSize: 13 }}>
                <CampoResumo label="Nome" valor={form.condutor_nome} />
                <CampoResumo label="CPF" valor={form.condutor_cpf} />
                <CampoResumo label="Nascimento" valor={fmtData(form.condutor_nascimento)} />
                <CampoResumo label="Endereço" valor={[form.condutor_endereco, form.condutor_numero].filter(Boolean).join(', ') || null} />
                <CampoResumo label="Complemento" valor={form.condutor_complemento} />
                <CampoResumo label="Bairro" valor={form.condutor_bairro} />
              </div>
              <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 13 }}>
                <CampoResumo label="Cidade/UF" valor={[form.condutor_cidade, form.condutor_estado].filter(Boolean).join('/') || null} />
                <CampoResumo label="CEP" valor={form.condutor_cep} />
              </div>

              {subLabelSublinhado('Valores')}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 13 }}>
                <CampoResumo label="Valor da Franquia" valor={form.valor_franquia} />
                {form.tipo_sinistro === 'Perda Parcial' && <CampoResumo label="Valor do Conserto" valor={form.valor_conserto} />}
                {(form.tipo_sinistro === 'Perda Total (Colisão)' || form.tipo_sinistro === 'Perda Total (Roubo)') && <CampoResumo label="Valor da Indenização" valor={form.valor_indenizacao} />}
              </div>

              {subLabelSublinhado('Oficina')}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, fontSize: 13 }}>
                <CampoResumo label="Nome" valor={oficinaSelecionadaObj?.nome} />
                <CampoResumo label="Telefone" valor={oficinaSelecionadaObj?.telefone} />
                <CampoResumo label="Responsável" valor={oficinaSelecionadaObj?.responsavel} />
              </div>
            </div>

            {/* ===== CARD ANEXOS ===== */}
            <div className="card" style={{ paddingTop: 14, paddingBottom: 16 }}>
              {sec('Anexos')}
              <AnexosSinistro id={sinistroId} basePath="/sinistros" />
            </div>

            {/* ===== CARD TERCEIROS ===== */}
            <div className="card" style={{ paddingTop: 14, paddingBottom: 16 }}>
              {sec('Terceiros')}
              <TerceirosSinistro sinistroId={sinistroId} temTerceiros={form.tem_terceiros} oficinas={oficinas} onOficinaCriada={onOficinaCriada} onVer={onVerTerceiro} />
            </div>
          </div>
        </div>
      )}
      {pedirSenhaMasterExcluir && (
        <SenhaMasterModal
          titulo="Excluir sinistro?"
          mensagem="Confirme com a Senha Master para excluir este sinistro definitivamente."
          onCancelar={() => setPedirSenhaMasterExcluir(false)}
          onConfirmar={confirmarExclusaoComSenha}
        />
      )}
    </div>
  )
}

function CampoResumo({ label, valor }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: '#999', marginBottom: 2 }}>{label}</div>
      <div style={{ color: '#333' }}>{valor || '—'}</div>
    </div>
  )
}

// ===== Página principal =====
// Sinistros do segurado e Terceiros (RCF) aparecem juntos na mesma tabela, cada um normalizado
// pro mesmo formato de linha — "registro" distingue quem é quem, "pertenceA" só existe no Terceiro
// (nome do segurado do sinistro a que ele pertence).
const normalizarSegurado = (s) => ({
  _tipo: 'segurado', id: s.id, numero: s.numero_sinistro,
  nome: s.cliente_nome, pertenceA: null,
  apolice: s.numero_apolice || s.emissao_numero_apolice,
  seguradora: s.seguradora, tipo: s.tipo_sinistro || '',
  ocorrencia: s.data_ocorrencia, status: s.status,
})
const normalizarTerceiro = (t) => ({
  _tipo: 'terceiro', id: t.sinistro_id, terceiroId: t.id, numero: t.numero_sinistro || t.sinistro_numero_segurado,
  nome: t.nome, pertenceA: t.segurado_nome,
  apolice: t.numero_apolice || t.emissao_numero_apolice,
  seguradora: t.seguradora, tipo: t.tipo_dano ? `${t.tipo_dano}${t.subtipo_dano ? ` (${t.subtipo_dano})` : ''}` : '',
  ocorrencia: t.data_ocorrencia, status: t.status,
})

const colunasSinistros = [
  { key: 'registro',  label: 'Registro',    get: r => r._tipo === 'terceiro' ? 'Terceiro' : 'Segurado' },
  { key: 'numero',    label: 'Nº Sinistro', get: r => r.numero || '' },
  { key: 'nome',      label: 'Nome',        get: r => r.nome || '' },
  { key: 'apolice',   label: 'Apólice',     get: r => r.apolice || '' },
  { key: 'seguradora', label: 'Seguradora', get: r => r.seguradora || '' },
  { key: 'tipo',      label: 'Tipo',        get: r => r.tipo || '' },
  { key: 'ocorrencia', label: 'Ocorrência', get: r => r.ocorrencia || '', date: true },
  { key: 'status',    label: 'Status',      get: r => statusInfo(r.status).label },
]

// Filtro de status com varias opcoes marcadas ao mesmo tempo; nenhuma marcada = Todos.
function FiltroStatusMultiplo({ selecionados, onChange }) {
  const [aberto, setAberto] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    if (!aberto) return
    const fechar = (e) => { if (ref.current && !ref.current.contains(e.target)) setAberto(false) }
    document.addEventListener('mousedown', fechar)
    return () => document.removeEventListener('mousedown', fechar)
  }, [aberto])

  const alternar = (v) => onChange(selecionados.includes(v) ? selecionados.filter(s => s !== v) : [...selecionados, v])
  const resumo = selecionados.length === 0 ? 'Todos'
    : selecionados.length === 1 ? (statusOptions.find(s => s.value === selecionados[0])?.label || selecionados[0])
    : `${selecionados.length} status selecionados`

  const itemStyle = { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 14, whiteSpace: 'nowrap' }
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" className="form-select" onClick={() => setAberto(a => !a)} style={{ textAlign: 'left', width: '100%', cursor: 'pointer' }}>
        {resumo}
      </button>
      {aberto && (
        <div style={{ position: 'absolute', top: '100%', left: 0, minWidth: '100%', zIndex: 20, background: '#fff', border: '1px solid #ccc', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.12)', padding: '4px 0', marginTop: 2 }}>
          <label style={{ ...itemStyle, borderBottom: '1px solid #eee' }}>
            <input type="checkbox" checked={selecionados.length === 0} onChange={() => onChange([])} />
            Todos
          </label>
          {statusOptions.map(s => (
            <label key={s.value} style={itemStyle}>
              <input type="checkbox" checked={selecionados.includes(s.value)} onChange={() => alternar(s.value)} />
              {s.label}
            </label>
          ))}
        </div>
      )}
    </div>
  )
}

export default function Sinistros() {
  const navigate = useNavigate()
  const [sinistros, setSinistros] = useState([])
  const [terceirosTodos, setTerceirosTodos] = useState([])
  const [carregando, setCarregando] = useState(true)
  const [busca, setBusca] = useState('')
  // Filtro abre/fecha pelo botao (igual Apolices/Renovacoes); fechar nao limpa. Salvo em localStorage.
  const [filtroStatus, setFiltroStatus] = useState(() => { try { const f = JSON.parse(localStorage.getItem('sinistros.filtros')); return Array.isArray(f?.status) ? f.status : [] } catch { return [] } })
  const [filtroAberto, setFiltroAberto] = useState(() => { try { return !!JSON.parse(localStorage.getItem('sinistros.filtros'))?.aberto } catch { return false } })
  useEffect(() => {
    try { localStorage.setItem('sinistros.filtros', JSON.stringify({ status: filtroStatus, aberto: filtroAberto })) } catch { /* sem localStorage: so nao persiste */ }
  }, [filtroStatus, filtroAberto])
  const [sortCol, setSortCol] = useState('ocorrencia')
  const [sortDir, setSortDir] = useState('desc')
  const [modalAberto, setModalAberto] = useState(false)
  const [sinistroEditId, setSinistroEditId] = useState(null)
  const [apoliceParaNovo, setApoliceParaNovo] = useState(null)
  const [voltarPara, setVoltarPara] = useState(null)

  const toggleSort = (key) => {
    if (sortCol === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(key); setSortDir('asc') }
  }

  const carregar = () => {
    setCarregando(true)
    Promise.all([
      api.get('/sinistros').then(r => Array.isArray(r.data) ? r.data : []).catch(() => []),
      api.get('/sinistros/terceiros').then(r => Array.isArray(r.data) ? r.data : []).catch(() => []),
    ]).then(([s, t]) => { setSinistros(s); setTerceirosTodos(t) }).finally(() => setCarregando(false))
  }
  useEffect(() => { carregar() }, [])

  // Vindo do botão Ações > Sinistros na tela de Apólices: abre direto o form de Novo Sinistro
  // com a apólice já travada, em vez de o usuário ter que buscar de novo.
  useEffect(() => {
    const apoliceJson = sessionStorage.getItem('sinistroNovaApolice')
    if (apoliceJson) {
      sessionStorage.removeItem('sinistroNovaApolice')
      try {
        setApoliceParaNovo(JSON.parse(apoliceJson))
        setSinistroEditId(null)
        setModalAberto(true)
      } catch { /* ignora json invalido */ }
    }
  }, [])

  // Vindo do botão vermelho de Sinistro na tela de Renovações: abre direto o sinistro (o mais
  // recente daquela apólice) em vez de so filtrar a lista, e guarda pra onde "‹ Voltar" deve ir.
  useEffect(() => {
    const abrirId = sessionStorage.getItem('sinistroAbrirId')
    if (abrirId) {
      sessionStorage.removeItem('sinistroAbrirId')
      setApoliceParaNovo(null)
      setSinistroEditId(Number(abrirId))
      setModalAberto(true)
    }
    const voltar = sessionStorage.getItem('sinistroVoltarPara')
    if (voltar) { sessionStorage.removeItem('sinistroVoltarPara'); setVoltarPara(voltar) }
  }, [])

  // Vindo do botão "Novo Sinistro" da Home: abre direto o form de Novo Sinistro, sem apólice
  // travada (usuário busca normalmente).
  useEffect(() => {
    if (sessionStorage.getItem('sinistroAbrirNovo')) {
      sessionStorage.removeItem('sinistroAbrirNovo')
      setApoliceParaNovo(null)
      setSinistroEditId(null)
      setModalAberto(true)
    }
  }, [])

  const abrirNovo = () => { setApoliceParaNovo(null); setSinistroEditId(null); setModalAberto(true) }
  // Linha de Segurado abre o sinistro normal; linha de Terceiro abre a tela propria do terceiro
  // numa aba nova (terceiroId, normalizado em normalizarTerceiro).
  const abrirVer = (r) => {
    if (r._tipo === 'terceiro') { navigate(`/sinistros/terceiro/${r.terceiroId}`); return }
    setApoliceParaNovo(null); setSinistroEditId(r.id); setModalAberto(true)
  }
  const fecharModal = () => { setModalAberto(false); setSinistroEditId(null); setApoliceParaNovo(null) }
  const abrirTerceiro = (t) => navigate(`/sinistros/terceiro/${t.id}`)

  const registros = [...sinistros.map(normalizarSegurado), ...terceirosTodos.map(normalizarTerceiro)]

  const filtrados = registros.filter(r => {
    if (filtroStatus.length > 0 && !filtroStatus.includes(r.status)) return false
    if (busca.trim()) {
      const termo = busca.trim().toLowerCase()
      const alvo = `${r.nome} ${r.pertenceA || ''} ${r.numero || ''} ${r.apolice || ''}`.toLowerCase()
      if (!alvo.includes(termo)) return false
    }
    return true
  })
  const ordenados = sortRows(filtrados, colunasSinistros, sortCol, sortDir)
  const paginacao = usePaginacao(ordenados)

  return (
    <>
      {!modalAberto && (<>
      <div style={{ padding: '32px 24px 0' }}>
        <h2 style={{ margin: 0, fontSize: 28, fontWeight: 700 }}>Sinistros</h2>
        <div style={{ fontSize: 13, color: '#888', marginTop: 4, marginBottom: 20 }}>Acompanhe os sinistros dos segurados e de terceiros.</div>
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
          <input className="form-input" placeholder="Buscar por segurado, terceiro, nº do sinistro ou da apólice..." value={busca} onChange={e => setBusca(e.target.value)}
            style={{ maxWidth: 420, width: '100%', border: '2px solid rgba(var(--primary-rgb),0.15)', borderRadius: 10 }} />
          <button type="button" className="btn btn-secondary" style={{ flexShrink: 0, whiteSpace: 'nowrap' }} onClick={() => setFiltroAberto(v => !v)}>
            {filtroAberto ? 'Fechar filtros' : 'Abrir filtros'}{filtroStatus.length > 0 && <span style={{ color: 'var(--primary)', marginLeft: 6 }}>●</span>}
          </button>
          <div style={{ flex: 1 }} />
          <button className="btn btn-primary" onClick={abrirNovo}>+ Novo Sinistro</button>
        </div>
        {filtroAberto && (
          <div className="card" style={{ marginBottom: 16, padding: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12, alignItems: 'end' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Status</label>
                <FiltroStatusMultiplo selecionados={filtroStatus} onChange={setFiltroStatus} />
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" disabled={!filtroStatus.length} onClick={() => setFiltroStatus([])}>Limpar filtros</button>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="page-content" style={{ paddingTop: 0 }}>
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12, fontSize: 12, color: '#888' }}>
            {filtrados.length} {filtrados.length === 1 ? 'registro' : 'registros'}
          </div>
          <div className="table-wrap">
            <table className="table-borda-destaque table-compacta">
              <thead>
                <tr>
                  {colunasSinistros.map(c => (
                    <SortableTh key={c.key} colKey={c.key} sortCol={sortCol} sortDir={sortDir} onSort={toggleSort}>{c.label}</SortableTh>
                  ))}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {paginacao.itensPagina.map((r, i) => {
                  const st = statusInfo(r.status)
                  return (
                    <tr key={`${r._tipo}-${r.id}-${i}`}>
                      <td style={{ textAlign: 'center' }}><span className={`badge ${r._tipo === 'terceiro' ? 'badge-amber' : 'badge-blue'}`}>{r._tipo === 'terceiro' ? 'Terceiro' : 'Segurado'}</span></td>
                      <td style={{ textAlign: 'center' }}>{r.numero || '—'}</td>
                      <td style={{ fontWeight: 500 }}>
                        <div>{r.nome}</div>
                        {r.pertenceA && <div style={{ fontSize: 11, fontWeight: 400, color: '#888' }}>Segurado: {r.pertenceA}</div>}
                      </td>
                      <td style={{ textAlign: 'center' }}>{r.apolice || '—'}</td>
                      <td style={{ textAlign: 'center' }}>{r.seguradora || '—'}</td>
                      <td style={{ textAlign: 'center' }}>{r.tipo || '—'}</td>
                      <td style={{ textAlign: 'center' }}>{fmtData(r.ocorrencia)}</td>
                      <td style={{ textAlign: 'center' }}><span className={`badge ${st.cls}`}>{st.label}</span></td>
                      <td style={{ textAlign: 'center' }}><button type="button" className="btn btn-secondary btn-sm btn-icon" title="Ver" style={{ color: 'var(--primary)' }} onClick={() => abrirVer(r)}><IconVisualizar size={14} /></button></td>
                    </tr>
                  )
                })}
                {!carregando && ordenados.length === 0 && (
                  <tr><td colSpan={colunasSinistros.length + 1} style={{ textAlign: 'center', color: '#aaa', padding: 32 }}>Nenhum sinistro encontrado</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <Paginacao {...paginacao} rotulo="sinistro" />
        </div>
      </div>
      </>)}

      {modalAberto && (
        <SinistroModal
          sinistroId={sinistroEditId}
          apoliceInicial={apoliceParaNovo}
          onClose={fecharModal}
          onSaved={carregar}
          voltarLabel={voltarPara ? 'Renovações' : 'Sinistros'}
          onVoltar={voltarPara ? () => navigate(voltarPara) : null}
          onVerTerceiro={abrirTerceiro}
        />
      )}
    </>
  )
}

// Rota propria (aberta em nova aba, ver Sinistros() > abrirVer/abrirTerceiro) pra tela do Sinistro
// do Terceiro — nunca aberta dentro da mesma aba do sinistro do segurado.
export function SinistroTerceiroPage() {
  const { terceiroId } = useParams()
  const navigate = useNavigate()
  return (
    <div className="page-content">
      <SinistroTerceiroModal
        terceiroId={Number(terceiroId)}
        onClose={() => navigate('/sinistros')}
        onVerSegurado={(sinistroIdSegurado) => {
          sessionStorage.setItem('sinistroAbrirId', String(sinistroIdSegurado))
          navigate('/sinistros')
        }}
      />
    </div>
  )
}
